import { buildInputEventData, InputLike } from './input-signals';
import { IdleTimer } from './idle-timer';

/**
 * ValidiformRecorder - Browser Bundle v4.0 PRODUCTION
 * Easy-to-embed session recording with complete CSS preservation
 * 
 * 🎨 CSS INLINING SOLUTION FOR BLUE HEADERS & STYLING PRESERVATION 🎨
 * 
 * Features:
 * - 🎨 Complete CSS inlining during recording for pixel-perfect replay
 * - 🏗️ Extensible form detection supporting 20+ form types  
 * - 🎯 Priority-based detection (WordPress: 90, NicePages: 80)
 * - 📦 Modular design with BaseFormDetector + specific detectors
 * - 🔄 60% code reduction through shared functionality
 * - 🚀 Ready for rapid expansion to new form types
 * - 💙 Preserves blue headers and original website styling
 * 
 * CSS Preservation:
 * - Fetches and inlines external stylesheets during recording
 * - Preserves blue headers like "We'd Love to Hear from You"
 * - Captures WordPress and NicePages styling
 * - Maintains form styling and branding elements
 * 
 * Supported Form Types:
 * - WordPress (Contact Form 7, Gravity Forms, WPForms, etc.)
 * - NicePages (forms.nicepagesrv.com integration)
 * - Extensible registry for future form types
 */

interface RecorderConfig {
  apiUrl: string;
  projectId: string;
  apiKey?: string;
  bufferSize?: number;
  flushInterval?: number;
  trackSensitiveData?: boolean;
  trackMouseMovement?: boolean;
  captureFormSubmissions?: boolean;
  stopOnFormSubmit?: boolean;
  captureOnButtonClick?: boolean;  // Capture form data on submit button click (for AJAX forms)
  enableHtmlMinification?: boolean;
  endSessionOnNavigation?: boolean;  // End session when URL changes (SPA navigation detection)
  continueOnNavigation?: boolean;    // Continue session with new snapshot on navigation (default: false)
  captureSnapshotDebounce?: number;  // Debounce time for navigation snapshot capture in ms (default: 100)
  maxSnapshotsPerSession?: number;   // Maximum snapshots per session (default: 50)
  inactivityTimeoutMs?: number;      // Idle cutoff: end session after this many ms with no user activity (default: 1_800_000 = 30 min); <= 0 disables
  debug?: boolean;
  visitorId?: number;
  visitorKey?: string;
  onSessionStart?: (sessionKey: string) => void;
  onSessionEnd?: () => void;
  onError?: (error: Error) => void;

  // Auto-detection for SPA forms that don't change URLs between steps
  autoDetectPageChanges?: boolean;       // Enable DOM-based page change detection (default: false)
  pageChangeDetectionMode?: 'visibility' | 'mutation' | 'both';  // Detection strategy (default: 'both')

  // Detection thresholds (tunable)
  visibilityChangeDebounce?: number;     // Debounce for visibility-triggered snapshots in ms (default: 150)
  mutationBurstThreshold?: number;       // Number of mutations to trigger snapshot (default: 15)
  mutationBurstWindow?: number;          // Time window for mutation burst in ms (default: 200)
  minContentChangePercent?: number;      // Minimum content change to trigger snapshot (default: 30)
}

interface EventData {
  type: string;
  time: number;
  path?: string;
  data: any;
  name?: string;
  snapshotIndex?: number;  // Index of the snapshot this event belongs to (for multi-snapshot sessions)
}

/**
 * Navigation snapshot data structure
 * Represents a captured state during SPA navigation
 */
interface NavigationSnapshot {
  snapshotIndex: number;
  eventTime: number;
  navigation: {
    type: 'initial' | 'pushState' | 'replaceState' | 'popstate' | 'hashchange' | 'manual';
    fromUrl?: string;
    toUrl: string;
    pathChanged: boolean;
  };
  html: string;
  title: string;
  initialState: {
    size: { width: number; height: number };
    scroll: { top: number; left: number };
  };
}

/**
 * Pending snapshot queue item
 * Used to queue navigation snapshots when concurrent captures are in progress
 */
interface PendingSnapshotItem {
  source: 'pushState' | 'replaceState' | 'popstate' | 'hashchange' | 'manual';
  fromUrl: string;
  toUrl: string;
  queuedAt: number;
}

/**
 * Form Detection Architecture Interfaces
 */
interface FormDetector {
  name: string;
  priority: number;
  detect(): HTMLFormElement[];
  setupMonitoring(form: HTMLFormElement): void;
  checkSuccess(form: HTMLFormElement): boolean;
  isEnabled(): boolean;
}


interface SuccessPattern {
  selector: string;
  checkVisible?: boolean;
  checkContent?: boolean;
}

interface FormDetectorConfig {
  enabled: boolean;
  priority: number;
  lazyLoad?: boolean;
}

/**
 * Centralized registry for form detection strategies
 * Supports priority-based detection and lazy loading
 */
class FormDetectorRegistry {
  private detectors: Map<string, FormDetector> = new Map();
  private config: Map<string, FormDetectorConfig> = new Map();
  private recorder: ValidiformRecorder;
  private initialized: boolean = false;

  constructor(recorder: ValidiformRecorder) {
    this.recorder = recorder;
  }

  /**
   * Register a form detector with optional configuration
   */
  register(detector: FormDetector, config?: Partial<FormDetectorConfig>): void {
    const detectorConfig: FormDetectorConfig = {
      enabled: true,
      priority: detector.priority,
      lazyLoad: false,
      ...config
    };

    this.detectors.set(detector.name, detector);
    this.config.set(detector.name, detectorConfig);
    
    this.recorder.log(`Registered form detector: ${detector.name} (priority: ${detectorConfig.priority})`);
  }

  /**
   * Initialize all registered detectors
   */
  initialize(): void {
    if (this.initialized) return;

    const enabledDetectors = Array.from(this.detectors.entries())
      .filter(([name, _]) => this.config.get(name)?.enabled)
      .sort(([nameA, _a], [nameB, _b]) => {
        const priorityA = this.config.get(nameA)?.priority || 0;
        const priorityB = this.config.get(nameB)?.priority || 0;
        return priorityB - priorityA; // Higher priority first
      });

    for (const [, detector] of enabledDetectors) {
      if (detector.isEnabled()) {
        this.setupDetector(detector);
      }
    }

    this.initialized = true;
    this.recorder.log(`Initialized ${enabledDetectors.length} form detectors`);
  }

  /**
   * Setup individual detector
   */
  private setupDetector(detector: FormDetector): void {
    try {
      const forms = detector.detect();
      forms.forEach(form => {
        const ubId = form.getAttribute('data-ub-id');
        if (ubId) {
          detector.setupMonitoring(form);
          this.recorder.log(`${detector.name}: Monitoring form with ubId ${ubId}`);
        }
      });
    } catch (error) {
      this.recorder.error(`Error setting up detector ${detector.name}:`, error);
    }
  }

  /**
   * Get detector by name
   */
  getDetector(name: string): FormDetector | undefined {
    return this.detectors.get(name);
  }

  /**
   * Get all registered detector names
   */
  getDetectorNames(): string[] {
    return Array.from(this.detectors.keys());
  }

  /**
   * Enable/disable a specific detector
   */
  setDetectorEnabled(name: string, enabled: boolean): void {
    const config = this.config.get(name);
    if (config) {
      config.enabled = enabled;
      this.recorder.log(`${enabled ? 'Enabled' : 'Disabled'} detector: ${name}`);
    }
  }
}

/**
 * Base class for form detectors providing common functionality
 */
abstract class BaseFormDetector implements FormDetector {
  protected recorder: ValidiformRecorder;
  
  abstract name: string;
  abstract priority: number;

  constructor(recorder: ValidiformRecorder) {
    this.recorder = recorder;
  }

  abstract detect(): HTMLFormElement[];
  abstract setupMonitoring(form: HTMLFormElement): void;
  abstract checkSuccess(form: HTMLFormElement): boolean;

  isEnabled(): boolean {
    return true; // Override in subclasses if needed
  }

  /**
   * Common success pattern checking
   */
  protected checkSuccessPatterns(form: HTMLFormElement, patterns: SuccessPattern[]): boolean {
    for (const pattern of patterns) {
      const element = form.querySelector(pattern.selector);
      if (element) {
        const htmlElement = element as HTMLElement;
        
        // Check visibility if required
        if (pattern.checkVisible && htmlElement.offsetParent === null) {
          continue;
        }
        
        // Check content if required
        if (pattern.checkContent && !htmlElement.textContent?.trim()) {
          continue;
        }
        
        return true;
      }
    }
    return false;
  }

  /**
   * Common field clearing detection
   */
  protected checkFieldClearing(form: HTMLFormElement, threshold: number = 0.7): boolean {
    const inputs = form.querySelectorAll('input[type="text"], input[type="email"], textarea');
    let clearedFields = 0;
    
    inputs.forEach(input => {
      if ((input as HTMLInputElement).value === '') {
        clearedFields++;
      }
    });
    
    return inputs.length > 0 && clearedFields / inputs.length > threshold;
  }

  /**
   * Common form monitoring setup
   */
  protected setupCommonMonitoring(form: HTMLFormElement): void {
    const ubId = form.getAttribute('data-ub-id');
    if (!ubId) return;

    // Track if we've already captured this form to prevent duplicates
    const capturedKey = `captured_${ubId}_${Date.now()}`;
    (form as any).__capturedKey = capturedKey;

    // Monitor form submission - but form submission is already handled by handleFormSubmit
    // So we don't need to capture again here
    form.addEventListener('submit', (_e) => {
      this.recorder.log(`${this.name}: Form submission event detected (already handled by main recorder)`);
    });
  }
}

/**
 * NicePages form detector
 * Handles forms using NicePages service (forms.nicepagesrv.com)
 */
class NicePagesDetector extends BaseFormDetector {
  name = 'NicePages';
  priority = 80; // High priority for NicePages forms

  private successPatterns: SuccessPattern[] = [
    { selector: '.u-form-send-success', checkVisible: true, checkContent: true },
    { selector: '.u-form-send-message.u-form-send-success', checkVisible: true },
    { selector: '[class*="success"]', checkVisible: true },
    { selector: '[class*="thank"]', checkVisible: true }
  ];

  detect(): HTMLFormElement[] {
    const selectors = [
      'form[action*="nicepagesrv.com"]',
      'form[action*="nicepage"]', 
      'form.u-form',
      'form[class*="u-form"]'
    ];

    const forms: HTMLFormElement[] = [];
    selectors.forEach(selector => {
      document.querySelectorAll<HTMLFormElement>(selector).forEach(form => {
        forms.push(form);
      });
    });

    this.recorder.log(`${this.name}: Detected ${forms.length} forms`);
    return forms;
  }

  setupMonitoring(form: HTMLFormElement): void {
    this.setupCommonMonitoring(form);
    this.setupAggressiveFormCapture(form);
    this.setupNicePagesSpecificHandling(form);
  }

  /**
   * Aggressive form data capture on ANY button click within form area
   */
  private setupAggressiveFormCapture(form: HTMLFormElement): void {
    // Capture form data on ANY button click within the form
    form.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      
      // Check if clicked element is any kind of button or clickable element
      if (target.tagName === 'BUTTON' || 
          target.tagName === 'INPUT' ||
          target.classList.contains('u-btn') ||
          target.classList.contains('btn') ||
          target.getAttribute('role') === 'button' ||
          target.style.cursor === 'pointer') {
        
        this.recorder.log(`${this.name}: Button/clickable element clicked in form - ${target.tagName}.${target.className}`);
        
        // DO NOT capture immediately - wait for actual form submission
        const ubId = form.getAttribute('data-ub-id');
        if (ubId) {
          this.recorder.log(`${this.name}: Button clicked in form ${ubId} - waiting for actual submission`);
          // this.captureFormDataImmediately(form, parseInt(ubId)); // DISABLED - too early!
        }
        
        // Also schedule success check
        setTimeout(() => {
          this.checkSuccess(form);
        }, 1000);
      }
    });
  }

  /**
   * NicePages-specific form handling
   */
  private setupNicePagesSpecificHandling(form: HTMLFormElement): void {
    // Monitor specific NicePages submit patterns
    const nicePagesSelectors = [
      '.u-btn-submit',
      '.u-btn-1', // Common NicePages button class
      '.u-btn-2',
      '.u-btn-3',
      'input[type="submit"]', 
      'button[type="submit"]',
      '[class*="u-btn"]',
      '[class*="submit"]'
    ];

    nicePagesSelectors.forEach(btnSelector => {
      const buttons = form.querySelectorAll(btnSelector);
      buttons.forEach(button => {
        button.addEventListener('click', () => {
          this.recorder.log(`${this.name}: NicePages submit button clicked - ${btnSelector}`);
          
          // DO NOT capture immediately - wait for actual form submission
          const ubId = form.getAttribute('data-ub-id');
          if (ubId) {
            this.recorder.log(`${this.name}: Submit button clicked in form ${ubId} - waiting for actual submission`);
            // this.captureFormDataImmediately(form, parseInt(ubId)); // DISABLED - too early!
          }
        });
      });
    });

    // Also monitor form section for any changes that might indicate submission
    const formSection = form.closest('.u-section, .u-form-section, [class*="u-section"]');
    if (formSection) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'attributes') {
            // Check if success message appeared
            setTimeout(() => {
              this.checkSuccess(form);
            }, 500);
          }
        });
      });
      
      observer.observe(formSection, {
        childList: true,
        attributes: true,
        subtree: true
      });
    }
  }

  /**
   * Immediately capture form data without waiting for success detection
   */
  private captureFormDataImmediately(form: HTMLFormElement, formUbId: number): void {
    try {
      // Check if we've already captured this form recently
      const capturedKey = (form as any).__capturedKey;
      const alreadyCaptured = (form as any).__alreadyCaptured;
      
      if (alreadyCaptured === capturedKey) {
        this.recorder.log(`${this.name}: Form already captured, skipping duplicate`);
        return;
      }
      
      this.recorder.log(`${this.name}: Capturing form data immediately`);
      this.recorder.captureFormSubmission(form, formUbId);
      
      // Mark as captured to prevent duplicates
      (form as any).__alreadyCaptured = capturedKey;
    } catch (error) {
      this.recorder.error(`${this.name}: Error capturing form data immediately:`, error);
    }
  }

  checkSuccess(form: HTMLFormElement): boolean {
    // Check if we've already captured this form
    const capturedKey = (form as any).__capturedKey;
    const alreadyCaptured = (form as any).__alreadyCaptured;
    
    if (alreadyCaptured === capturedKey) {
      this.recorder.log(`${this.name}: Form already captured, skipping success check`);
      return false;
    }

    // IMPORTANT: Prevent premature success detection
    // Don't consider empty fields as "success" if the form was never filled
    const filledFieldsCount = Array.from(form.elements)
      .filter(el => {
        const input = el as HTMLInputElement;
        return input.type !== 'hidden' && 
               input.type !== 'submit' && 
               input.type !== 'button' &&
               input.value && 
               input.value.trim() !== '';
      }).length;
    
    // If less than 2 non-hidden fields have been filled, it's too early to detect success
    if (filledFieldsCount < 2) {
      this.recorder.log(`${this.name}: Too early for success check - only ${filledFieldsCount} fields filled`);
      return false;
    }

    // Check success message patterns
    if (this.checkSuccessPatterns(form, this.successPatterns)) {
      this.recorder.log(`${this.name}: Success detected via success message`);
      const ubId = form.getAttribute('data-ub-id');
      if (ubId) {
        // Only capture if not already captured
        if (alreadyCaptured !== capturedKey) {
          this.recorder.captureFormSubmission(form, parseInt(ubId));
          (form as any).__alreadyCaptured = capturedKey;
        }
      }
      return true;
    }

    // Check field clearing - but only if fields were previously filled
    // Store initial filled state
    if (!(form as any).__initialFilledCount) {
      (form as any).__initialFilledCount = filledFieldsCount;
    }
    
    // Only check for field clearing if we had significant data before
    if ((form as any).__initialFilledCount >= 3 && this.checkFieldClearing(form, 0.7)) {
      this.recorder.log(`${this.name}: Success detected via field clearing (had ${(form as any).__initialFilledCount} fields before)`);
      const ubId = form.getAttribute('data-ub-id');
      if (ubId) {
        // Only capture if not already captured
        if (alreadyCaptured !== capturedKey) {
          this.recorder.captureFormSubmission(form, parseInt(ubId));
          (form as any).__alreadyCaptured = capturedKey;
        }
      }
      return true;
    }

    return false;
  }
}

/**
 * WordPress form detector
 * Handles various WordPress form plugins (Contact Form 7, Gravity Forms, etc.)
 */
class WordPressDetector extends BaseFormDetector {
  name = 'WordPress';
  priority = 90; // Highest priority for WordPress forms

  private successPatterns: SuccessPattern[] = [
    { selector: '.wpcf7-mail-sent-ok', checkVisible: true },
    { selector: '.gform_confirmation_message', checkVisible: true },
    { selector: '.wpforms-confirmation-container', checkVisible: true },
    { selector: '.ninja-forms-success-msg', checkVisible: true },
    { selector: '.forminator-response-message', checkVisible: true },
    { selector: '.ff_form_success', checkVisible: true }
  ];

  detect(): HTMLFormElement[] {
    const selectors = [
      '.wpcf7-form',           // Contact Form 7
      '.gform_wrapper form',   // Gravity Forms
      '.wpforms-form',         // WPForms
      '.ninja-forms-form',     // Ninja Forms
      '.forminator-custom-form', // Forminator
      '.fluentform',           // Fluent Forms
      '.elementor-form',       // Elementor Forms
      'form[name*="contact"]', // Generic contact forms
      'form[class*="wp-"]'     // WordPress-specific classes
    ];

    const forms: HTMLFormElement[] = [];
    selectors.forEach(selector => {
      document.querySelectorAll<HTMLFormElement>(selector).forEach(form => {
        forms.push(form);
      });
    });

    this.recorder.log(`${this.name}: Detected ${forms.length} forms`);
    return forms;
  }

  setupMonitoring(form: HTMLFormElement): void {
    this.setupCommonMonitoring(form);
    this.setupAjaxInterception(form);
    this.setupSubmitButtonMonitoring(form);
  }

  private setupAjaxInterception(_form: HTMLFormElement): void {
    const recorder = this.recorder;
    
    // Intercept WordPress admin-ajax.php and wp-json calls
    const originalFetch = window.fetch;
    window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
      const url = input.toString();
      
      if ((url.includes('admin-ajax.php') || url.includes('wp-json')) && 
          init?.method === 'POST' && init?.body) {
        recorder.log(`WordPress: AJAX detected - ${url}`);
        // Check if this relates to our monitored form
        // Additional form-specific logic could be added here
      }
      
      return originalFetch.call(window, input, init);
    };
  }

  private setupSubmitButtonMonitoring(form: HTMLFormElement): void {
    const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => {
        this.recorder.log(`${this.name}: Submit button clicked`);
        setTimeout(() => {
          this.checkSuccess(form);
        }, 2000); // WordPress forms may take longer
      });
    }
  }

  checkSuccess(form: HTMLFormElement): boolean {
    // Check if we've already captured this form
    const capturedKey = (form as any).__capturedKey;
    const alreadyCaptured = (form as any).__alreadyCaptured;
    
    if (alreadyCaptured === capturedKey) {
      this.recorder.log(`${this.name}: Form already captured, skipping success check`);
      return false;
    }

    // Check WordPress success patterns
    if (this.checkSuccessPatterns(form, this.successPatterns)) {
      this.recorder.log(`${this.name}: Success detected via WordPress success message`);
      const ubId = form.getAttribute('data-ub-id');
      if (ubId) {
        // Only capture if not already captured
        if (alreadyCaptured !== capturedKey) {
          this.recorder.captureFormSubmission(form, parseInt(ubId));
          (form as any).__alreadyCaptured = capturedKey;
        }
      }
      return true;
    }

    // Check for form state changes (hidden, success class, etc.)
    if (form.style.display === 'none' || 
        form.classList.contains('submitted') || 
        form.classList.contains('success')) {
      this.recorder.log(`${this.name}: Success detected via form state change`);
      const ubId = form.getAttribute('data-ub-id');
      if (ubId) {
        // Only capture if not already captured
        if (alreadyCaptured !== capturedKey) {
          this.recorder.captureFormSubmission(form, parseInt(ubId));
          (form as any).__alreadyCaptured = capturedKey;
        }
      }
      return true;
    }

    return false;
  }
}

class ValidiformRecorder {
  private config: RecorderConfig;
  private sessionKey: string | null = null;
  private startTime: number = 0;
  private lastEventTime: number = 0;  // Track last event time for accurate duration
  private events: EventData[] = [];
  // Attribute names to skip — static form metadata already captured in snapshot HTML
  private static readonly ATTR_BLOCKLIST = new Set(['name', 'type']);
  // Style throttle: track last style event time per element path
  private styleThrottleMap: Map<string, number> = new Map();
  private static readonly STYLE_THROTTLE_MS = 200;

  private observer: MutationObserver | null = null;
  private flushTimer: any = null;
  private isRecording: boolean = false;
  private isInitialized: boolean = false;
  private eventIndex: number = 0;
  private pathCache: Map<Element, string> = new Map();
  private formDetectorRegistry: FormDetectorRegistry;
  private lastInputEventTime: Map<string, number> = new Map();
  private inputThrottleDelay: number = 200; // ms between input events per field
  private inputPollTimer: number | null = null;
  private capturedInputValues: Map<string, string> = new Map();
  private currentlyFocusedElement: HTMLElement | null = null;
  private recentCaptures: Set<string> = new Set();  // For deduplication of button-click captures
  // TCPA consent de-dupe, keyed by `${sessionKey}-${formUbId}` → capture time.
  // One user submission can reach captureTcpaConsent() from several directions
  // (the native submit path via captureFormSubmission(), the button-click/AJAX
  // path via captureVirtualSubmission(), and the form-detector call sites), so
  // the guard lives in captureTcpaConsent() itself rather than at any one call
  // site — a consent record must be written once per submission, not once per
  // path that noticed it.
  private recentTcpaCaptures: Map<string, number> = new Map();
  private static readonly TCPA_DEDUPE_WINDOW_MS = 3000;
  // A form submitted while idle-ended: replayed once resumeFromIdle() creates
  // the fresh session (finding 8). Latest submission wins if more than one
  // arrives before the resume completes.
  private pendingIdleSubmission: { form: HTMLFormElement; formUbId: number } | null = null;
  private lastSubmissionTime: number = 0;
  private recordedUrl: string = '';  // URL when session started, for SPA navigation detection
  private originalPushState: typeof history.pushState | null = null;
  private originalReplaceState: typeof history.replaceState | null = null;

  // SPA Navigation Multi-Snapshot Support
  private snapshotIndex: number = 0;           // Current snapshot index (0 = initial)
  private pendingSnapshotTimer: ReturnType<typeof setTimeout> | null = null;  // Debounce timer
  private isCapturingSnapshot: boolean = false;  // Prevent concurrent captures
  private pendingSnapshots: PendingSnapshotItem[] = [];  // Queue for pending snapshots
  private isProcessingQueue: boolean = false;  // Prevent concurrent queue processing

  // Auto-detection state for SPA forms without URL changes
  private stepContainers: Set<Element> = new Set();    // Tracked form step containers
  private mutationBuffer: { time: number; target: Element }[] = [];  // Buffer for mutation burst detection
  private lastAutoSnapshot: number = 0;          // Timestamp of last auto-triggered snapshot
  private contentHashMap: Map<Element, string> = new Map();  // Track content changes for step containers

  // Content-based detection (works without specific class names)
  private contentObserver: MutationObserver | null = null;
  private lastContentHash: string = '';
  private contentChangeDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // URL path polling for SPA navigation detection (Fix 2)
  private lastUrlPath: string = '';
  private urlPollingInterval: ReturnType<typeof setInterval> | null = null;

  // Inactivity cutoff (Part 1 of session-timeout spec)
  private idleTimer: IdleTimer | null = null;
  private isIdleEnded: boolean = false;   // true between onIdle firing and a successful resume
  private isResuming: boolean = false;    // guards against overlapping resume createSession() calls
  // Serializes idle-end and resume transitions so a fast activity burst can't
  // start a new session while the old one is still being flushed/ended (both
  // touch this.sessionKey / this.lastEventTime and must not interleave).
  private idleTransition: Promise<void> = Promise.resolve();
  // Event types that represent direct, unambiguous user interaction — the ONLY
  // ones allowed to reset the idle timer. Everything else (scroll, mouse/size
  // telemetry, SPA navigation, and all MutationObserver-driven DOM-change events
  // — add/remove/attribute/text — can fire without the user having touched
  // anything) must NOT keep a session alive indefinitely. Deliberately an
  // allowlist rather than a blocklist of "known non-user" types: a future event
  // type must opt in to count as activity instead of silently counting by default.
  private static readonly IDLE_ACTIVITY_TYPES = new Set(['click', 'input', 'change']);

  // Public getters for debugging
  public get recording(): boolean { return this.isRecording; }
  public get initialized(): boolean { return this.isInitialized; }
  public get session(): string | null { return this.sessionKey; }
  public get eventBuffer(): EventData[] { return [...this.events]; }
  public get capturedValues(): Map<string, string> { return new Map(this.capturedInputValues); }

  constructor(config: RecorderConfig) {
    this.config = {
      bufferSize: 50,  // Increased from 10 to reduce flush frequency
      flushInterval: 5000,  // Increased from 1s to 5s
      trackSensitiveData: false,
      trackMouseMovement: false,  // Disabled by default to reduce events
      enableHtmlMinification: true,  // Enable by default for smaller payloads
      endSessionOnNavigation: true,  // End session on SPA navigation by default
      continueOnNavigation: false,   // Don't continue by default (backward compat)
      captureSnapshotDebounce: 300,  // 300ms debounce for navigation snapshot capture
      maxSnapshotsPerSession: 50,    // Maximum 50 snapshots per session
      inactivityTimeoutMs: 1_800_000, // 30 minutes; <= 0 disables the idle cutoff
      debug: false,
      // Auto-detection defaults (disabled by default for backward compat)
      autoDetectPageChanges: false,
      pageChangeDetectionMode: 'both',
      visibilityChangeDebounce: 150,
      mutationBurstThreshold: 15,
      mutationBurstWindow: 200,
      minContentChangePercent: 30,
      ...config
    };

    if (!this.config.apiUrl || !this.config.projectId) {
      throw new Error('apiUrl and projectId are required');
    }

    // Validate conflicting navigation options
    if (this.config.endSessionOnNavigation && this.config.continueOnNavigation) {
      console.warn('[ValidiformRecorder] Both endSessionOnNavigation and continueOnNavigation are true. continueOnNavigation takes precedence - session will continue with snapshots on navigation.');
    }

    // Clean up API URL - remove trailing slash and warn about common mistakes
    this.config.apiUrl = this.config.apiUrl.replace(/\/$/, '');

    // Warn about potential double /api/ issues in debug mode
    if (this.config.debug && this.config.apiUrl.endsWith('/api')) {
      console.warn('[ValidiformRecorder] apiUrl ends with /api - this may work but is not recommended. Use base URL instead.', {
        current: this.config.apiUrl,
        recommended: this.config.apiUrl.replace(/\/api$/, '')
      });
    }

    // Initialize form detector registry
    this.formDetectorRegistry = new FormDetectorRegistry(this);

    // Inactivity cutoff: one idle timer for the lifetime of this recorder instance.
    this.idleTimer = new IdleTimer({
      timeoutMs: this.config.inactivityTimeoutMs!,
      onIdle: () => this.handleSessionIdle(),
      onResume: () => this.handleIdleResume()
    });
  }

  public log(...args: any[]) {
    if (this.config.debug) {
      console.log('[ValidiformRecorder]', ...args);
    }
  }

  public error(...args: any[]) {
    console.error('[ValidiformRecorder]', ...args);
    if (this.config.onError) {
      this.config.onError(new Error(args.join(' ')));
    }
  }

  private buildApiUrl(path: string): string {
    // Construct URL, avoiding double /api/ if apiUrl already ends with /api
    const baseUrl = this.config.apiUrl.endsWith('/api') 
      ? this.config.apiUrl 
      : `${this.config.apiUrl}/api`;
    return `${baseUrl}${path}`;
  }

  private isBot(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    if (!ua) return true;
    // Common bot/crawler patterns
    if (/bot|crawl|spider|slurp|bingpreview|facebookexternalhit|mediapartners|google|baidu|yandex|duckduck|archive\.org|semrush|ahrefs|mj12bot|dotbot|petalbot|bytespider|gptbot|claudebot|anthropic/i.test(ua)) return true;
    // Headless browsers
    if ((navigator as any).webdriver === true) return true;
    return false;
  }

  async start(): Promise<void> {
    if (this.isBot()) {
      this.log('Bot detected, skipping recording');
      return;
    }

    if (this.config.debug && window.location.href.includes('60-20.com')) {
      this.log('[DEBUG] start() called');
    }

    if (this.isRecording) {
      this.log('Already recording');
      return;
    }

    try {
      await this.createSession();
      this.startRecording();
      this.isInitialized = true;
      this.isIdleEnded = false;
      this.idleTimer?.start();
      this.log('Recording started', { sessionKey: this.sessionKey });
    } catch (error) {
      this.error('Failed to start recording:', error);
      throw error;
    }
  }

  stop(): void {
    if (!this.isRecording || !this.isInitialized) {
      this.log('Not recording or not initialized');
      return;
    }

    // Idle cutoff no longer applies once the session is deliberately stopped.
    this.idleTimer?.stop();
    // If the session was already ended by the idle cutoff, its sessionKey is
    // stale (kept only so in-flight handlers can no-op cleanly) — don't send a
    // second /end request for it, which would re-inflate its duration with the
    // idle wait. Only a genuinely active session needs ending here.
    const wasIdleEnded = this.isIdleEnded;
    this.isIdleEnded = false;
    // A form submitted while idle-ended belongs to whatever session
    // resumeFromIdle() would have created; once stop() has run there's no
    // session left to replay it against.
    this.pendingIdleSubmission = null;

    this.stopRecording();
    this.styleThrottleMap.clear();

    // Only end session if we have a session key and it wasn't already ended
    if (this.sessionKey && !wasIdleEnded) {
      this.endSession();
    }

    this.log('Recording stopped');

    // Call the onSessionEnd callback if provided — but not when the idle path
    // already fired it once via endSession() (finding 9). If a resumeFromIdle()
    // is still in flight, it will end the orphaned session itself and fire
    // onSessionEnd() from there instead (finding 1).
    if (this.config.onSessionEnd && !wasIdleEnded) {
      this.config.onSessionEnd();
    }
  }

  // ---------------------------------------------------------------------
  // Inactivity cutoff (Part 1 of the session-timeout spec)
  // ---------------------------------------------------------------------

  /**
   * IdleTimer's onIdle callback. Synchronous by contract. Marks isIdleEnded
   * and stops the flush timer HERE, synchronously, before enqueueing the
   * actual (async) idle-end work onto idleTransition — otherwise activity
   * arriving in the window between onIdle firing and beginIdleEnd() actually
   * running would see isIdleEnded still false and get buffered into (or
   * flushed against) the session beginIdleEnd() is about to end out from
   * under it. With the flag set first, that activity's onResume/addEvent path
   * correctly falls through to scheduleIdleResume(), which queues behind
   * beginIdleEnd() on the same idleTransition chain instead of racing it.
   */
  private handleSessionIdle(): void {
    if (!this.isRecording || !this.sessionKey || this.isIdleEnded) {
      return;
    }

    this.stopFlushTimer();
    this.isIdleEnded = true;

    // Duration = last real activity offset from session start, not the idle
    // wait itself — capture it now, before any further activity can change it.
    const idleDuration = this.lastEventTime;

    this.idleTransition = this.idleTransition
      .then(() => this.beginIdleEnd(idleDuration))
      .catch((error) => this.error('[IDLE] Error ending idle session:', error));
  }

  /**
   * IdleTimer's onResume callback (fires once, on the first activity after
   * onIdle). Just delegates to scheduleIdleResume() — the same entry point
   * addEvent()/captureFormSubmission() use to retry a previously failed resume.
   */
  private handleIdleResume(): void {
    this.scheduleIdleResume();
  }

  /**
   * Enqueues resumeFromIdle() onto the shared transition chain, guarded so a
   * burst of activity events only schedules one resume attempt at a time.
   * Safe (and expected) to call repeatedly, including as a retry after a
   * previous resumeFromIdle() failed.
   */
  private scheduleIdleResume(): void {
    if (!this.isRecording || !this.isIdleEnded || this.isResuming) {
      return;
    }
    this.isResuming = true;
    this.idleTransition = this.idleTransition
      .then(() => this.resumeFromIdle())
      .catch((error) => this.error('[IDLE] Error resuming from idle:', error))
      .finally(() => {
        this.isResuming = false;
      });
  }

  /**
   * Flushes and ends the current session because inactivityTimeoutMs elapsed
   * with no user activity. isIdleEnded is already true and the flush timer
   * already stopped — handleSessionIdle() did both synchronously before
   * enqueueing this — so any activity racing in during the flush/end network
   * calls below is queued behind this in idleTransition rather than
   * interleaving with it (see resumeFromIdle()).
   */
  private async beginIdleEnd(idleDuration: number): Promise<void> {
    this.log('[IDLE] Inactivity timeout reached — flushing and ending session', { sessionKey: this.sessionKey });

    try {
      await this.flushEvents();
    } catch (error) {
      this.error('[IDLE] Failed to flush events before idle end:', error);
    }

    // duration: 0 reads as "still open" server-side — never report a zero
    // idle duration even when the session had no activity at all.
    await this.endSession({ reason: 'idle', durationOverride: Math.max(idleDuration, 1) });

    this.log('[IDLE] Session idle-ended; waiting for activity to resume');
  }

  /**
   * Starts a fresh session in response to the first activity after an idle
   * end (or retries a previously failed attempt). Runs strictly after any
   * in-flight beginIdleEnd() via idleTransition, so it never races the old
   * session's flush/end against the new session's create.
   *
   * The activity event that triggered this call is not re-emitted — it may
   * be lost, since session creation (with its fresh HTML snapshot) is async.
   */
  private async resumeFromIdle(): Promise<void> {
    if (!this.isRecording || !this.isIdleEnded) {
      return;
    }

    this.log('[IDLE] Activity detected — starting a new session');

    // Reset everything scoped to a session before creating the new one,
    // mirroring what start() gets for free on the very first session.
    this.resetPerSessionState();

    try {
      await this.createSession(); // assigns a fresh sessionKey + startTime, fires onSessionStart

      // stop() can run synchronously while createSession()'s fetch is still in
      // flight above — it can't await this chain (must stay sync for
      // beforeunload) so it already tore down recording and, seeing
      // isIdleEnded true at the time, assumed the idle path would handle
      // ending things and skipped its own endSession()/onSessionEnd(). The
      // session we just created is therefore an orphan nobody else will ever
      // end: finish it off immediately instead of arming a flush timer for a
      // recorder that's already stopped.
      if (!this.isRecording) {
        this.log('[IDLE] stop() ran during resume — ending orphaned session', { sessionKey: this.sessionKey });
        await this.endSession({ reason: 'aborted-resume', durationOverride: 1 });
        return;
      }

      this.isIdleEnded = false;
      this.startFlushTimer();

      // Replay a form submission that arrived while idle-ended (finding 8),
      // now that there's a fresh session to attach it to. Only if the form is
      // still in the document — a submission whose page has since navigated
      // away isn't worth resurrecting.
      if (this.pendingIdleSubmission) {
        const { form, formUbId } = this.pendingIdleSubmission;
        this.pendingIdleSubmission = null;
        if (form.isConnected) {
          this.captureFormSubmission(form, formUbId);
        }
      }

      this.log('[IDLE] Resumed with new session', { sessionKey: this.sessionKey });
    } catch (error) {
      this.error('[IDLE] Failed to start a new session after idle activity:', error);
      // Stay idle-ended — the next activity's addEvent()/captureFormSubmission()
      // call invokes scheduleIdleResume() again and retries.
    }
  }

  /**
   * Clears everything scoped to a single session so a fresh one starts empty
   * — the same state start() gets for free on the very first session (its
   * fields are all fresh from the constructor), which is why this isn't
   * called there too. Used by resumeFromIdle() before createSession().
   */
  private resetPerSessionState(): void {
    this.events = [];
    this.eventIndex = 0;
    this.lastEventTime = 0;
    this.snapshotIndex = 0;

    // Per-field dedupe/throttle state belongs to the old session: a value
    // identical to the pre-idle one must still be captured into the fresh one.
    this.capturedInputValues.clear();
    this.lastInputEventTime.clear();
    this.styleThrottleMap.clear();
    this.recentCaptures.clear();
    // Keyed by the old session key, so entries can never match the fresh one;
    // cleared anyway so the map doesn't carry dead keys across a rollover.
    this.recentTcpaCaptures.clear();

    // Element-path cache is keyed by live Element references; harmless to
    // keep, but stale entries only ever grow, so drop them at each rollover.
    this.pathCache.clear();
    this.currentlyFocusedElement = null;

    // Pending navigation-snapshot debounce/capture state belongs to the ended
    // session's timeline.
    if (this.pendingSnapshotTimer) {
      clearTimeout(this.pendingSnapshotTimer);
      this.pendingSnapshotTimer = null;
    }
    this.isCapturingSnapshot = false;

    // Content-change detection hashes (autoDetectPageChanges) are relative to
    // the ended session's DOM/content timeline.
    this.contentHashMap.clear();
    this.lastContentHash = '';
  }

  private async createSession(): Promise<void> {
    // Assign data-ub-id to all elements first
    this.assignElementIds();
    
    // Generate or retrieve visitor ID and key
    const visitorId = this.getOrCreateVisitorId();
    const visitorKey = this.getOrCreateVisitorKey();
    
    // Store in config for use throughout the session
    this.config.visitorId = visitorId;
    this.config.visitorKey = visitorKey;
    
    // Capture HTML with size optimization and enhanced CSS
    const optimizedHtml = await this.captureOptimizedHtml();
    
    const payload = {
      url: window.location.href,
      title: document.title,
      html: optimizedHtml,
      visitor: {
        id: visitorId,
        key: visitorKey,
        user_agent: navigator.userAgent
      },
      initial_state: {
        size: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        scroll: {
          top: window.pageYOffset || document.documentElement.scrollTop,
          left: window.pageXOffset || document.documentElement.scrollLeft
        }
      }
    };

    const headers: any = {
      'Content-Type': 'application/json'
    };

    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey;
    }

    const response = await fetch(
      this.buildApiUrl(`/projects/${this.config.projectId}/ub/pages`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to create session: ${response.statusText}`);
    }

    const result = await response.json();
    // Handle Laravel-compatible response format
    this.sessionKey = result.data?.key || result.sessionKey;
    this.startTime = Date.now();
    this.recordedUrl = window.location.href;  // Store URL for SPA navigation detection

    // Store visitor key if provided
    if (result.data?.visitor?.key) {
      localStorage.setItem('ub.visitorKey', result.data.visitor.key);
    }

    if (this.config.onSessionStart && this.sessionKey) {
      this.config.onSessionStart(this.sessionKey);
    }
  }

  private startRecording(): void {
    this.isRecording = true;
    this.assignElementIds();
    this.setupEventListeners();
    this.setupMutationObserver();
    this.startFlushTimer();
    this.startInputPolling();

    // Initialize auto-detection for SPA forms without URL changes
    if (this.config.autoDetectPageChanges) {
      // Class-based detection (looks for specific patterns like .step, .panel, etc.)
      this.identifyStepContainers();
      this.initializeVisibilityTracking();

      // Content-based detection (works without specific class names)
      // This is the primary detection method - monitors form content changes
      this.initContentChangeDetection();

      // URL path polling (Fix 2 for PLL) - catches pushState that bypasses our interception
      this.initUrlPathPolling();

      this.log('[AUTO_DETECT] 🔍 Page change auto-detection enabled (class-based + content-based + URL polling)');
    }
  }

  private stopRecording(): void {
    this.isRecording = false;
    this.removeEventListeners();
    this.stopMutationObserver();
    this.stopFlushTimer();
    this.stopInputPolling();
    this.flushEvents();

    // Clear any pending snapshot capture
    if (this.pendingSnapshotTimer) {
      clearTimeout(this.pendingSnapshotTimer);
      this.pendingSnapshotTimer = null;
    }

    // Clear the snapshot queue
    this.pendingSnapshots = [];
    this.isProcessingQueue = false;

    // Clear auto-detection state
    this.stepContainers.clear();
    this.mutationBuffer = [];
    this.contentHashMap.clear();
    this.lastAutoSnapshot = 0;

    // Clean up content-based detection
    this.destroyContentChangeDetection();

    // Clean up URL path polling
    this.destroyUrlPathPolling();
  }

  private assignElementIds(): void {
    let idCounter = 1;
    
    // Get all elements that don't already have data-ub-id
    const elements = document.querySelectorAll('*:not([data-ub-id])');
    
    if (this.config.debug && window.location.href.includes('60-20.com')) {
      this.log(`[DEBUG] assignElementIds: Assigning IDs to ${elements.length} elements without IDs`);
      const totalElements = document.querySelectorAll('*').length;
      const existingIds = document.querySelectorAll('[data-ub-id]').length;
      this.log(`[DEBUG] Total elements: ${totalElements}, Already have IDs: ${existingIds}`);
    }
    
    // Find the highest existing ID to continue from
    let maxId = 0;
    document.querySelectorAll('[data-ub-id]').forEach(element => {
      const id = parseInt(element.getAttribute('data-ub-id') || '0', 10);
      if (id > maxId) maxId = id;
    });
    
    idCounter = maxId + 1;
    
    // Only assign IDs to elements that don't have them
    elements.forEach(element => {
      element.setAttribute('data-ub-id', String(idCounter++));
      
      // Validate input patterns during initial setup
      if (element.tagName === 'INPUT') {
        this.validateInputPattern(element as HTMLInputElement);
      }
    });
    
    // Store the last used ID for consistency
    if ((window as any).__validiformLastId === undefined) {
      (window as any).__validiformLastId = idCounter;
    }
    
    if (this.config.debug && window.location.href.includes('60-20.com')) {
      const formsWithIds = document.querySelectorAll('form[data-ub-id]').length;
      const inputsWithIds = document.querySelectorAll('input[data-ub-id], textarea[data-ub-id]').length;
      this.log(`[DEBUG] After assignment: ${formsWithIds} forms and ${inputsWithIds} inputs have data-ub-id`);
    }
  }

  private assignElementIdsToSubtree(root: Element): void {
    // Find the highest existing data-ub-id to continue from
    let maxId = 0;
    document.querySelectorAll('[data-ub-id]').forEach(element => {
      const id = parseInt(element.getAttribute('data-ub-id') || '0', 10);
      if (id > maxId) maxId = id;
    });
    
    // Use global counter if available
    let idCounter = (window as any).__validiformLastId || maxId + 1;
    
    // Log for debugging
    if (this.config.debug) this.log(`[DEBUG] assignElementIdsToSubtree: Starting from ID ${idCounter}`);
    
    // Assign IDs only to elements in the subtree that don't have one
    const elements = [root, ...Array.from(root.querySelectorAll('*'))];
    elements.forEach(element => {
      if (!element.hasAttribute('data-ub-id')) {
        element.setAttribute('data-ub-id', String(idCounter++));
      }
      
      // Validate input patterns
      if (element.tagName === 'INPUT') {
        this.validateInputPattern(element as HTMLInputElement);
      }
    });
    
    // Update global counter
    (window as any).__validiformLastId = idCounter;
  }

  private getElementPath(element: Element): string {
    // Always prefer data-ub-id if available
    const ubId = element.getAttribute('data-ub-id');
    if (ubId) {
      return `[data-ub-id="${ubId}"]`;
    }
    
    // Cache check for fallback paths
    if (this.pathCache.has(element)) {
      return this.pathCache.get(element)!;
    }

    // Fallback to CSS selector path
    const path = [];
    let current: Element | null = element;

    while (current && current !== document.documentElement) {
      let selector = current.tagName.toLowerCase();
      if (current.id) {
        selector += `#${current.id}`;
      }
      if (current.className && typeof current.className === 'string') {
        const classes = current.className.trim().split(/\s+/).filter(c => c);
        if (classes.length > 0) {
          selector += `.${classes.join('.')}`;
        }
      }

      const siblings = current.parentElement ? 
        Array.from(current.parentElement.children).filter(c => c.tagName === current!.tagName) : [];
      
      if (siblings.length > 1) {
        const index = siblings.indexOf(current as Element) + 1;
        selector += `:nth-of-type(${index})`;
      }

      path.unshift(selector);
      current = current.parentElement;
    }

    const fullPath = path.join(' > ');
    this.pathCache.set(element, fullPath);
    return fullPath;
  }

  private addEvent(type: string, data: any, element?: Element): void {
    // Feed the idle timer from unambiguous user-originated events only (see
    // IDLE_ACTIVITY_TYPES) — scroll/mouse/size telemetry, SPA navigation, and
    // MutationObserver-driven DOM-change events can all fire without the user
    // having touched anything and must not reset the inactivity countdown.
    if (ValidiformRecorder.IDLE_ACTIVITY_TYPES.has(type)) {
      this.idleTimer?.activity();

      // Session is idle-ended: listeners stay attached so we can detect resume
      // activity, but nothing gets buffered into the dead session below until
      // resumeFromIdle() has created a fresh one. scheduleIdleResume() is safe
      // to call repeatedly — it no-ops once a resume is already in flight, and
      // also covers the retry case where a previous resume attempt failed (the
      // idle timer's own one-shot onResume won't fire again on its own).
      if (this.isIdleEnded) {
        this.scheduleIdleResume();
      }
    }

    // This specific event is allowed to be lost — session creation for the
    // resume is async, see resumeFromIdle().
    if (this.isIdleEnded) {
      return;
    }

    const eventTime = Date.now() - this.startTime;
    const event: EventData = {
      type,
      time: eventTime,
      data,
      snapshotIndex: this.snapshotIndex  // Include current snapshot index for multi-snapshot sessions
    };

    // Track the last event time for accurate duration calculation
    this.lastEventTime = Math.max(this.lastEventTime, eventTime);

    if (element) {
      event.path = this.getElementPath(element);
    }

    this.events.push(event);
    this.log('Event added:', event);

    if (this.events.length >= this.config.bufferSize!) {
      this.flushEvents();
    }
  }

  private async flushEvents(): Promise<void> {
    if (this.events.length === 0 || !this.sessionKey) return;

    // Captured before the first await: if the session moves on (idle-ends
    // and/or resumes to a new key) while this flush is in flight, comparing
    // against this — not this.sessionKey — tells us these events and this
    // response no longer belong to the live session.
    const flushKey = this.sessionKey;
    const eventsToSend = [...this.events];
    this.events = [];

    try {
      const headers: any = {
        'Content-Type': 'application/json'
      };

      if (this.config.apiKey) {
        headers['X-API-Key'] = this.config.apiKey;
      }

      const eventsWithIndex = eventsToSend.map(event => ({
        ...event,
        index: this.eventIndex++
      }));

      const response = await fetch(
        this.buildApiUrl(`/ub/pages/${flushKey}/events`),
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ events: eventsWithIndex }),
          keepalive: true // Keep request alive during page unload
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          // A 429 for a session that already idle-ended or resumed to a new
          // key while this request was in flight says nothing about the live
          // session — drop the batch and leave the recorder alone. Never
          // clear isIdleEnded just to be able to call endSession() here.
          if (this.isIdleEnded || this.sessionKey !== flushKey) {
            this.log(`[IDLE] Ignoring 429 for stale session ${flushKey}`);
            return;
          }
          // Live session hit its event limit — stop recording, do not re-queue events.
          console.warn('[Validiform] Session event limit reached. Recording stopped.');
          this.stopRecording();
          // The session is dead; make sure idle detection can't fire (or resume) against it.
          this.idleTimer?.stop();
          await this.endSession();
          return;
        }
        const errorText = await response.text();
        this.error(`Event flush failed with status ${response.status}: ${errorText}`);
        throw new Error(`Failed to send events: ${response.statusText}`);
      }

      this.log(`Flushed ${eventsToSend.length} events`);
    } catch (error) {
      this.error('Failed to flush events:', error);
      // Only re-queue onto the session these events actually belong to — if
      // the session idle-ended and/or resumed to a new key while this
      // request was in flight, re-queuing would leak the old session's
      // events into the new one's buffer/index sequence. Drop them instead.
      if (this.sessionKey === flushKey && !this.isIdleEnded) {
        this.events = eventsToSend.concat(this.events);
      } else {
        this.log(`[IDLE] Dropping ${eventsToSend.length} events from stale session ${flushKey} after session moved on`);
      }
    }
  }

  private setupEventListeners(): void {
    // Click events
    document.addEventListener('click', this.handleClick, true);

    // Input events - multiple event types for better framework compatibility
    document.addEventListener('input', this.handleInput, true);
    document.addEventListener('change', this.handleChange, true);
    
    // Additional events for frameworks that use synthetic events
    document.addEventListener('keyup', this.handleInput, true);
    
    // Dedicated blur handling for better tab navigation capture
    document.addEventListener('blur', this.handleBlur, true);
    document.addEventListener('focusout', this.handleBlur, true);
    
    // Special handling for focus events to capture field interactions
    document.addEventListener('focus', this.handleFocus, true);
    
    // Tab key detection for keyboard navigation
    document.addEventListener('keydown', this.handleKeyDown, true);

    // Form submission
    document.addEventListener('submit', this.handleFormSubmit, true);

    // Enhanced form submission detection
    this.setupAjaxInterception();
    this.setupFormButtonTracking();
    
    // Initialize new form detection architecture
    this.initializeFormDetectors();
    
    // Legacy form detection (disabled during transition testing)
    // this.setupWordPressFormDetection();
    // this.setupNicePagesFormDetection();
    this.setupCustomEventInterception();

    // Scroll events
    window.addEventListener('scroll', this.handleScroll, true);

    // Mouse movement (if enabled)
    if (this.config.trackMouseMovement) {
      document.addEventListener('mousemove', this.handleMouseMove, true);
    }

    // Window resize
    window.addEventListener('resize', this.handleResize);

    // SPA navigation detection - end session or capture snapshots when URL changes
    // Enable if either endSessionOnNavigation or continueOnNavigation is true
    if (this.config.endSessionOnNavigation || this.config.continueOnNavigation) {
      this.setupNavigationDetection();
    }
  }

  private removeEventListeners(): void {
    document.removeEventListener('click', this.handleClick, true);
    document.removeEventListener('input', this.handleInput, true);
    document.removeEventListener('change', this.handleChange, true);
    document.removeEventListener('keyup', this.handleInput, true);
    document.removeEventListener('blur', this.handleBlur, true);
    document.removeEventListener('focusout', this.handleBlur, true);
    document.removeEventListener('focus', this.handleFocus, true);
    document.removeEventListener('keydown', this.handleKeyDown, true);
    document.removeEventListener('submit', this.handleFormSubmit, true);
    window.removeEventListener('scroll', this.handleScroll, true);
    document.removeEventListener('mousemove', this.handleMouseMove, true);
    window.removeEventListener('resize', this.handleResize);

    // Clean up SPA navigation detection
    this.cleanupNavigationDetection();
  }

  /**
   * Set up SPA (Single Page Application) navigation detection.
   * This intercepts History API calls and listens for popstate/hashchange events
   * to detect when the user navigates to a new page without a full page reload.
   */
  private setupNavigationDetection(): void {
    this.log('[SPA_NAV] Setting up navigation detection for URL changes');

    // Store original History API methods
    this.originalPushState = history.pushState.bind(history);
    this.originalReplaceState = history.replaceState.bind(history);

    // Intercept pushState
    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      const result = this.originalPushState!(...args);
      this.handleNavigationChange('pushState');
      return result;
    };

    // Intercept replaceState
    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      const result = this.originalReplaceState!(...args);
      this.handleNavigationChange('replaceState');
      return result;
    };

    // Listen for browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState);

    // Listen for hash changes (older SPA routing method)
    window.addEventListener('hashchange', this.handleHashChange);

    this.log(`[SPA_NAV] Navigation detection active. Initial URL: ${this.recordedUrl}`);
  }

  /**
   * Clean up SPA navigation detection - restore original History API methods
   */
  private cleanupNavigationDetection(): void {
    // Restore original History API methods
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
      this.originalPushState = null;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }

    // Remove event listeners
    window.removeEventListener('popstate', this.handlePopState);
    window.removeEventListener('hashchange', this.handleHashChange);

    // Clear any pending snapshot capture timer
    if (this.pendingSnapshotTimer) {
      clearTimeout(this.pendingSnapshotTimer);
      this.pendingSnapshotTimer = null;
    }

    // Clear the pending snapshot queue
    if (this.pendingSnapshots.length > 0) {
      this.log(`[SPA_NAV] Clearing ${this.pendingSnapshots.length} pending snapshots during cleanup`);
      this.pendingSnapshots = [];
    }

    // Reset snapshot capture state
    this.isCapturingSnapshot = false;
    this.isProcessingQueue = false;
  }

  /**
   * Handle popstate event (browser back/forward)
   */
  private handlePopState = (): void => {
    this.handleNavigationChange('popstate');
  };

  /**
   * Handle hashchange event
   */
  private handleHashChange = (): void => {
    this.handleNavigationChange('hashchange');
  };

  /**
   * Handle URL change - determine if this is a significant navigation
   * that should end the current session or capture a new snapshot.
   */
  private handleNavigationChange(source: 'pushState' | 'replaceState' | 'popstate' | 'hashchange'): void {
    const newUrl = window.location.href;
    const oldUrl = this.recordedUrl;

    // Skip if URL hasn't actually changed
    if (newUrl === oldUrl) {
      this.log(`[SPA_NAV] ${source}: URL unchanged, ignoring`);
      return;
    }

    // Parse URLs to compare paths
    try {
      const oldUrlObj = new URL(oldUrl);
      const newUrlObj = new URL(newUrl);

      // Check if this is a significant navigation (different path)
      // We consider it significant if:
      // 1. The pathname is different (not just query params or hash)
      // 2. OR the origin is different (rare but possible)
      const pathChanged = oldUrlObj.pathname !== newUrlObj.pathname;
      const originChanged = oldUrlObj.origin !== newUrlObj.origin;

      if (pathChanged || originChanged) {
        this.log(`[SPA_NAV] ${source}: Significant navigation detected`);
        this.log(`[SPA_NAV]   From: ${oldUrl}`);
        this.log(`[SPA_NAV]   To: ${newUrl}`);
        this.log(`[SPA_NAV]   Path changed: ${pathChanged}, Origin changed: ${originChanged}`);

        // FIX 3: Update recordedUrl IMMEDIATELY before queueing (prevent race condition)
        // This ensures rapid navigations don't compare against stale recordedUrl
        this.recordedUrl = newUrl;
        // Also update lastUrlPath to prevent duplicate snapshots from URL polling
        this.lastUrlPath = newUrlObj.pathname;

        // Check if we should continue with a new snapshot instead of ending
        if (this.config.continueOnNavigation) {
          this.log('[SPA_NAV] continueOnNavigation enabled - capturing new snapshot');
          this.debouncedCaptureNavigationSnapshot(source, oldUrl, newUrl);
        } else {
          // End the current session (original behavior)
          this.log('[SPA_NAV] Ending session due to page navigation');
          this.stop();

          // Note: We don't automatically start a new session here because:
          // 1. The new page might have its own recorder instance
          // 2. Auto-starting could cause duplicate recordings
          // 3. The website owner should configure their SPA to re-initialize recording if needed
        }
      } else {
        // Only query params or hash changed - not a full navigation
        this.log(`[SPA_NAV] ${source}: Minor URL change (query/hash only), continuing session`);

        // Record the navigation event for playback context
        this.addEvent('navigation', {
          type: source,
          from: oldUrl,
          to: newUrl,
          pathChanged: false
        });

        // Update recorded URL even for minor changes
        this.recordedUrl = newUrl;
      }
    } catch (error) {
      this.log(`[SPA_NAV] ${source}: Error parsing URLs`, error);
    }
  }

  /**
   * Queue snapshot capture to handle rapid navigation events.
   * Instead of dropping snapshots during rapid navigation, we queue them.
   * This ensures all SPA navigation steps are captured reliably.
   */
  private queueNavigationSnapshot(
    source: 'pushState' | 'replaceState' | 'popstate' | 'hashchange' | 'manual',
    fromUrl: string,
    toUrl: string
  ): void {
    // Check if we've hit the maximum snapshots limit
    const maxSnapshots = this.config.maxSnapshotsPerSession || 50;
    const totalPending = this.snapshotIndex + this.pendingSnapshots.length;
    if (totalPending >= maxSnapshots) {
      this.log(`[SPA_NAV] Maximum snapshots (${maxSnapshots}) reached - stopping session`);
      this.stop();
      return;
    }

    // Check if this URL is already in the queue (deduplication)
    const existingIndex = this.pendingSnapshots.findIndex(p => p.toUrl === toUrl);
    if (existingIndex >= 0) {
      // Update existing queue item with new source (keep fresher data)
      this.pendingSnapshots[existingIndex] = { source, fromUrl, toUrl, queuedAt: Date.now() };
      this.log(`[SPA_NAV] Updated existing queued snapshot for ${toUrl}`);
    } else {
      // Add to queue
      this.pendingSnapshots.push({ source, fromUrl, toUrl, queuedAt: Date.now() });
      this.log(`[SPA_NAV] Queued snapshot for ${toUrl} (queue size: ${this.pendingSnapshots.length})`);
    }

    // Trigger queue processing with a short debounce
    // This allows rapid navigations to settle slightly before processing
    if (this.pendingSnapshotTimer) {
      clearTimeout(this.pendingSnapshotTimer);
    }

    const debounceTime = this.config.captureSnapshotDebounce || 100;  // Reduced from 300ms to 100ms
    this.pendingSnapshotTimer = setTimeout(() => {
      this.pendingSnapshotTimer = null;
      this.processSnapshotQueue();
    }, debounceTime);
  }

  /**
   * Process the snapshot queue sequentially.
   * This ensures snapshots are captured in order without overlapping.
   */
  private async processSnapshotQueue(): Promise<void> {
    // Prevent concurrent queue processing
    if (this.isProcessingQueue) {
      this.log('[SPA_NAV] Queue processing already in progress');
      return;
    }

    // Verify we have an active session
    if (!this.sessionKey || !this.isRecording) {
      this.log('[SPA_NAV] No active session, clearing queue');
      this.pendingSnapshots = [];
      return;
    }

    // Check if queue is empty
    if (this.pendingSnapshots.length === 0) {
      this.log('[SPA_NAV] Snapshot queue is empty');
      return;
    }

    this.isProcessingQueue = true;
    this.log(`[SPA_NAV] Processing snapshot queue (${this.pendingSnapshots.length} items)`);

    try {
      while (this.pendingSnapshots.length > 0 && this.isRecording && this.sessionKey) {
        const item = this.pendingSnapshots.shift();
        if (!item) break;

        // Check if snapshot is stale (older than 30 seconds)
        const age = Date.now() - item.queuedAt;
        if (age > 30000) {
          this.log(`[SPA_NAV] Skipping stale snapshot (${age}ms old): ${item.toUrl}`);
          continue;
        }

        await this.captureNavigationSnapshot(item.source, item.fromUrl, item.toUrl);

        // Small delay between captures to prevent overwhelming the server
        if (this.pendingSnapshots.length > 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    } catch (error) {
      this.error('[SPA_NAV] Error processing snapshot queue:', error);
    } finally {
      this.isProcessingQueue = false;
      this.log('[SPA_NAV] Finished processing snapshot queue');
    }
  }

  /**
   * @deprecated Use queueNavigationSnapshot instead
   * Kept for backward compatibility
   */
  private debouncedCaptureNavigationSnapshot(
    source: 'pushState' | 'replaceState' | 'popstate' | 'hashchange' | 'manual',
    fromUrl: string,
    toUrl: string
  ): void {
    this.queueNavigationSnapshot(source, fromUrl, toUrl);
  }

  /**
   * Capture a navigation snapshot when the page content changes due to SPA navigation.
   * This captures the new DOM state and sends it to the API.
   */
  private async captureNavigationSnapshot(
    source: 'pushState' | 'replaceState' | 'popstate' | 'hashchange' | 'manual',
    fromUrl: string,
    toUrl: string
  ): Promise<void> {
    // If already capturing, re-queue this snapshot instead of dropping it
    if (this.isCapturingSnapshot) {
      this.log('[SPA_NAV] Already capturing snapshot, re-queuing for later');
      // Add back to queue with high priority (at the front)
      this.pendingSnapshots.unshift({ source, fromUrl, toUrl, queuedAt: Date.now() });
      return;
    }

    // Verify we have an active session. isIdleEnded counts as not-recording —
    // this.sessionKey is a stale/dead key at that point, so a snapshot upload
    // would write against a session that no longer accepts writes.
    if (!this.sessionKey || !this.isRecording || this.isIdleEnded) {
      if (this.isIdleEnded) {
        // The navigation itself is user-driven activity — resume instead of
        // dropping it silently. The post-resume createSession() already takes
        // a fresh snapshot, so this particular navigation snapshot is allowed
        // to be lost, same as any other event during the idle-ended window.
        this.log('[SPA_NAV] Session idle-ended, triggering resume instead of snapshotting a dead session');
        this.scheduleIdleResume();
      } else {
        this.log('[SPA_NAV] No active session, skipping snapshot capture');
      }
      return;
    }

    this.isCapturingSnapshot = true;
    this.log(`[SPA_NAV] Starting snapshot capture for navigation: ${source}`);

    try {
      // Wait for DOM to stabilize (SPA frameworks may still be rendering)
      await this.waitForDomStable();

      // Increment snapshot index for this new page state
      this.snapshotIndex++;
      this.log(`[SPA_NAV] Capturing snapshot ${this.snapshotIndex}`);

      // Re-assign element IDs to the new DOM
      this.assignElementIds();
      this.log('[SPA_NAV] Re-assigned element IDs to new DOM');

      // Capture fresh HTML snapshot with CSS preservation
      const html = await this.captureOptimizedHtml();
      this.log(`[SPA_NAV] Captured HTML snapshot (${html.length} chars)`);

      // Build snapshot data
      const snapshot: NavigationSnapshot = {
        snapshotIndex: this.snapshotIndex,
        eventTime: Date.now() - this.startTime,
        navigation: {
          type: source,
          fromUrl: fromUrl,
          toUrl: toUrl,
          pathChanged: true
        },
        html: html,
        title: document.title,
        initialState: {
          size: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          scroll: {
            top: window.pageYOffset || document.documentElement.scrollTop,
            left: window.pageXOffset || document.documentElement.scrollLeft
          }
        }
      };

      // Send snapshot to API
      await this.sendSnapshot(snapshot);

      // Update recorded URL to the new URL
      this.recordedUrl = toUrl;

      // Record a navigation event with the new snapshot index
      this.addEvent('navigation', {
        type: source,
        from: fromUrl,
        to: toUrl,
        pathChanged: true,
        snapshotIndex: this.snapshotIndex
      });

      this.log(`[SPA_NAV] Successfully captured and sent snapshot ${this.snapshotIndex}`);

    } catch (error) {
      this.error('[SPA_NAV] Failed to capture navigation snapshot:', error);
      // Continue recording even if snapshot capture fails
    } finally {
      this.isCapturingSnapshot = false;
    }
  }

  /**
   * Send a navigation snapshot to the API with retry logic.
   * Retries up to 3 times with exponential backoff on failure.
   */
  private async sendSnapshot(snapshot: NavigationSnapshot, maxRetries: number = 3): Promise<void> {
    if (!this.sessionKey) {
      throw new Error('No active session');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey;
    }

    const endpoint = this.buildApiUrl(`/ub/pages/${this.sessionKey}/snapshots`);
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        this.log(`[SPA_NAV] Sending snapshot to ${endpoint} (attempt ${attempt}/${maxRetries})`);

        // NOTE: Do NOT use keepalive here - it has a 64KB payload limit
        // SPA navigation doesn't unload the page, so keepalive isn't needed
        // The HTML + CSS snapshot easily exceeds 64KB, causing "Failed to fetch"
        const response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(snapshot)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to send snapshot: ${response.status} - ${errorText}`);
        }

        this.log('[SPA_NAV] Snapshot sent successfully');
        return;  // Success - exit retry loop
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (attempt < maxRetries) {
          // Exponential backoff: 1s, 2s, 4s...
          const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          this.log(`[SPA_NAV] Snapshot send failed (attempt ${attempt}), retrying in ${delayMs}ms...`);
          await new Promise(resolve => setTimeout(resolve, delayMs));
        } else {
          this.error(`[SPA_NAV] Snapshot send failed after ${maxRetries} attempts:`, lastError);
        }
      }
    }

    // All retries exhausted
    if (lastError) {
      throw lastError;
    }
  }

  /**
   * Wait for the DOM to stabilize after SPA navigation.
   * Uses MutationObserver to detect when DOM changes stop.
   */
  private waitForDomStable(timeout: number = 2000, quietPeriod: number = 100): Promise<void> {
    return new Promise((resolve) => {
      let timeoutId: ReturnType<typeof setTimeout>;
      let quietTimeoutId: ReturnType<typeof setTimeout>;
      let observer: MutationObserver | null = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (quietTimeoutId) clearTimeout(quietTimeoutId);
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      };

      const scheduleResolve = () => {
        // Clear any existing quiet timeout
        if (quietTimeoutId) clearTimeout(quietTimeoutId);

        // Schedule resolve after quiet period
        quietTimeoutId = setTimeout(() => {
          cleanup();
          this.log('[SPA_NAV] DOM stabilized');
          resolve();
        }, quietPeriod);
      };

      // Set up mutation observer
      observer = new MutationObserver(() => {
        // Reset quiet period on each mutation
        scheduleResolve();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      });

      // Start initial quiet period
      scheduleResolve();

      // Set maximum timeout
      timeoutId = setTimeout(() => {
        cleanup();
        this.log('[SPA_NAV] DOM stability timeout reached');
        resolve();
      }, timeout);
    });
  }

  /**
   * Public API to manually capture a snapshot of the current DOM state.
   * Use this when form steps change without URL changes and auto-detection isn't working.
   *
   * Example usage in React/Next.js:
   *   window.ValidiformRecorder?.captureSnapshot('Step 2: Personal Info');
   *
   * @param stepName - Optional description of the current step (for debugging)
   */
  public captureSnapshot(stepName?: string): void {
    if (!this.isRecording || !this.sessionKey) {
      this.log('[SPA_NAV] captureSnapshot: Not recording, ignoring');
      return;
    }

    if (!this.config.continueOnNavigation) {
      this.log('[SPA_NAV] captureSnapshot: continueOnNavigation disabled, ignoring');
      return;
    }

    const currentUrl = window.location.href;
    const description = stepName ? ` (${stepName})` : '';
    this.log(`[SPA_NAV] 📸 Manual snapshot requested${description}`);

    // Queue the snapshot with 'manual' source
    this.queueNavigationSnapshot('manual', currentUrl, currentUrl);
  }

  /**
   * Public API for manually notifying the recorder of a page view.
   * Useful for SPAs that don't use standard History API or for custom navigation scenarios.
   *
   * @param url - Optional URL to use (defaults to current window.location.href)
   */
  public trackPageView(url?: string): void {
    const newUrl = url || window.location.href;
    const oldUrl = this.recordedUrl;

    // Skip if URL hasn't changed
    if (newUrl === oldUrl) {
      this.log('[SPA_NAV] trackPageView: URL unchanged, ignoring');
      return;
    }

    // Skip if not recording
    if (!this.isRecording || !this.sessionKey) {
      this.log('[SPA_NAV] trackPageView: Not recording, ignoring');
      return;
    }

    this.log(`[SPA_NAV] trackPageView: Manual page view notification from ${oldUrl} to ${newUrl}`);

    // Check if continueOnNavigation is enabled
    if (this.config.continueOnNavigation) {
      this.debouncedCaptureNavigationSnapshot('manual', oldUrl, newUrl);
    } else {
      // Record as a navigation event but don't capture snapshot
      this.addEvent('navigation', {
        type: 'manual',
        from: oldUrl,
        to: newUrl,
        pathChanged: true
      });
      this.recordedUrl = newUrl;
    }
  }

  // ==========================================
  // AUTO-DETECTION: SPA Form Step Changes
  // ==========================================

  /**
   * Identify step containers in the DOM.
   * Looks for common multi-step form patterns:
   * - Elements with step-related classes (.step, .page, .panel)
   * - Elements with step attributes (data-step, data-page, aria-current)
   * - Direct children of wizard/form containers
   */
  private identifyStepContainers(): void {
    this.stepContainers.clear();
    this.contentHashMap.clear();

    const selectors = [
      '[class*="step"]:not(.stepper):not([class*="step-indicator"])',  // .step, .step-1, .form-step
      '[class*="page"]:not(body):not([class*="pagination"])',          // .page, .page-1
      '[class*="panel"]:not([class*="panel-header"])',                 // .panel, .tab-panel
      '[class*="slide"]:not([class*="slider"])',                       // .slide (for carousel-style forms)
      '[data-step]',                                                   // data-step="1"
      '[data-page]',                                                   // data-page="personal-info"
      '[data-active]',                                                 // data-active="true"
      '.wizard-content > div',                                         // Direct children of wizard
      '.form-wizard > div',                                            // Form wizard steps
      '.multi-step-form > div',                                        // Multi-step form containers
      'form > fieldset',                                               // Fieldset-based steps
      '[role="tabpanel"]',                                             // ARIA tab panels
      '[aria-hidden]',                                                 // Toggleable sections
    ];

    selectors.forEach(sel => {
      try {
        document.querySelectorAll(sel).forEach(el => {
          // IMPORTANT: Include elements even if currently hidden (clientHeight = 0)
          // We need to track hidden step containers so we can detect when they become visible
          // Use child count as a proxy for meaningful content since hidden elements have no height
          const hasChildren = el.children.length >= 1;
          const hasFormContent = !!el.querySelector('input, textarea, select, button');
          const hasTextContent = (el.textContent?.trim().length || 0) > 20;

          // For visible elements, also check size
          const isVisible = (el as HTMLElement).offsetHeight > 0;
          const hasSizeIfVisible = !isVisible || el.clientHeight > 50;

          // Include if has children AND (form content OR text content) AND reasonable size if visible
          if (hasChildren && (hasFormContent || hasTextContent) && hasSizeIfVisible) {
            this.stepContainers.add(el);
            // Only hash visible content - hidden elements get empty hash initially
            this.contentHashMap.set(el, isVisible ? this.hashContent(el) : '');
          }
        });
      } catch (e) {
        // Ignore invalid selectors
      }
    });

    if (this.stepContainers.size > 0) {
      this.log(`[AUTO_DETECT] Found ${this.stepContainers.size} potential step containers`);
      if (this.config.debug) {
        this.stepContainers.forEach(el => {
          const isVisible = (el as HTMLElement).offsetHeight > 0;
          this.log(`[AUTO_DETECT]   - ${this.describeElement(el)} (${isVisible ? 'visible' : 'hidden'})`);
        });
      }
    } else {
      this.log('[AUTO_DETECT] No step containers found - will rely on mutation burst detection');
    }
  }

  /**
   * Initialize visibility tracking for step containers.
   * Marks current visibility state of all tracked containers.
   */
  private initializeVisibilityTracking(): void {
    // Only set visibility tracking attributes - do NOT overwrite content hashes
    // Hashes were already set correctly in identifyStepContainers: visible=hash, hidden=''
    // Overwriting would break change detection for hidden-to-visible transitions
    this.stepContainers.forEach(el => {
      const isVisible = this.isElementVisible(el);
      el.setAttribute('data-ub-was-visible', String(isVisible));
      // Note: hash already set in identifyStepContainers() - don't overwrite here
    });
    this.log('[AUTO_DETECT] Initialized visibility tracking for step containers');
  }

  /**
   * Check if an element is currently visible to the user.
   */
  private isElementVisible(el: Element): boolean {
    const style = window.getComputedStyle(el);
    const htmlEl = el as HTMLElement;

    // Check CSS visibility
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    // Check HTML attributes
    if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    // Check common visibility classes
    if (el.classList.contains('hidden') || el.classList.contains('hide') ||
        el.classList.contains('d-none') || el.classList.contains('invisible')) {
      return false;
    }

    // Check for active/visible classes (positive indicators)
    const hasActiveClass = el.classList.contains('active') || el.classList.contains('show') ||
                          el.classList.contains('visible') || el.classList.contains('current');

    // Check offset dimensions (0x0 means hidden)
    if (htmlEl.offsetWidth === 0 && htmlEl.offsetHeight === 0 && !hasActiveClass) {
      return false;
    }

    return true;
  }

  /**
   * Check for visibility changes on a step container.
   * Triggered when attribute changes are detected.
   */
  private checkVisibilityChange(element: Element, changedAttr: string): void {
    const now = Date.now();
    const debounce = this.config.visibilityChangeDebounce || 150;

    // Debounce rapid visibility toggles
    if (now - this.lastAutoSnapshot < debounce) {
      return;
    }

    // Check if element became visible
    const isNowVisible = this.isElementVisible(element);
    const wasVisible = element.getAttribute('data-ub-was-visible') === 'true';

    if (isNowVisible && !wasVisible) {
      // Step container just became visible - this is likely a page change
      this.log(`[AUTO_DETECT] Step container became visible: ${this.describeElement(element)} (attr: ${changedAttr})`);

      // Verify content actually changed (not just toggle animation)
      const newHash = this.hashContent(element);
      const oldHash = this.contentHashMap.get(element);

      if (newHash !== oldHash) {
        this.lastAutoSnapshot = now;
        this.contentHashMap.set(element, newHash);
        this.triggerAutoSnapshot('visibility', element);
      } else {
        this.log(`[AUTO_DETECT] Content unchanged, skipping snapshot`);
      }
    }

    // Update visibility state tracking
    element.setAttribute('data-ub-was-visible', String(isNowVisible));
  }

  /**
   * Track mutations for burst detection.
   * Detects rapid DOM changes that indicate a page/step transition.
   */
  private trackMutationBurst(container: Element, _mutation: MutationRecord): void {
    const now = Date.now();
    const windowMs = this.config.mutationBurstWindow || 200;
    const threshold = this.config.mutationBurstThreshold || 15;
    const debounce = this.config.visibilityChangeDebounce || 150;

    // Debounce - don't trigger too frequently
    if (now - this.lastAutoSnapshot < debounce) {
      return;
    }

    // Add to buffer
    this.mutationBuffer.push({ time: now, target: container });

    // Clean old entries outside the window
    this.mutationBuffer = this.mutationBuffer.filter(m => now - m.time < windowMs);

    // Count mutations on this container
    const containerMutations = this.mutationBuffer.filter(m => m.target === container);

    if (containerMutations.length >= threshold) {
      this.log(`[AUTO_DETECT] Mutation burst detected: ${containerMutations.length} mutations in ${windowMs}ms on ${this.describeElement(container)}`);

      // Clear buffer for this container to prevent repeated triggers
      this.mutationBuffer = this.mutationBuffer.filter(m => m.target !== container);
      this.lastAutoSnapshot = now;

      // Wait for DOM to stabilize, then capture
      setTimeout(() => {
        this.triggerAutoSnapshot('mutation-burst', container);
      }, 100);
    }
  }

  /**
   * Trigger an automatic snapshot capture due to detected DOM changes.
   */
  private triggerAutoSnapshot(reason: 'visibility' | 'mutation-burst' | 'content-change', trigger: Element): void {
    if (!this.config.continueOnNavigation) {
      this.log(`[AUTO_DETECT] Would capture snapshot (${reason}) but continueOnNavigation is disabled`);
      return;
    }

    if (!this.isRecording || !this.sessionKey) {
      this.log(`[AUTO_DETECT] Not recording, skipping auto-snapshot`);
      return;
    }

    if (this.isCapturingSnapshot) {
      this.log(`[AUTO_DETECT] Snapshot capture already in progress, queuing`);
      this.queueNavigationSnapshot('manual', window.location.href, window.location.href);
      return;
    }

    this.log(`[AUTO_DETECT] 🎯 Triggering snapshot capture - reason: ${reason}, trigger: ${this.describeElement(trigger)}`);

    // Use existing snapshot mechanism with 'manual' source
    // This ensures the snapshot gets the current URL (unchanged) but fresh DOM
    this.queueNavigationSnapshot('manual', window.location.href, window.location.href);
  }

  /**
   * Generate a simple hash of element content for change detection.
   */
  private hashContent(el: Element): string {
    const text = el.textContent || '';
    const childCount = el.children.length;
    const inputValues = Array.from(el.querySelectorAll('input, select, textarea'))
      .map(i => (i as HTMLInputElement).value)
      .join('|');
    // Simple hash combining length, child count, and content preview
    return `${text.length}-${childCount}-${text.substring(0, 100)}-${inputValues.substring(0, 50)}`;
  }

  /**
   * Generate a human-readable description of an element for logging.
   */
  private describeElement(el: Element): string {
    if (el.id) {
      return `#${el.id}`;
    }
    if (el.className && typeof el.className === 'string') {
      const firstClass = el.className.trim().split(/\s+/)[0];
      if (firstClass) {
        return `.${firstClass}`;
      }
    }
    const dataStep = el.getAttribute('data-step');
    if (dataStep) {
      return `[data-step="${dataStep}"]`;
    }
    return el.tagName.toLowerCase();
  }

  // ==========================================
  // CSS VISIBILITY CLASS DETECTION (Fix 1 for PLL)
  // Detects when step containers toggle active/inactive classes
  // ==========================================

  /**
   * Check if an element is likely a step container based on its class/id/attributes.
   * This catches PLL's pattern where step-section elements toggle active/inactive classes.
   */
  private isLikelyStepContainer(el: Element): boolean {
    const className = (el.className || '').toString().toLowerCase();
    const id = (el.id || '').toLowerCase();

    // Match PLL's pattern: step-section, and common patterns
    return /step|page|panel|wizard|form-section|tab-content|slide|view|stage|phase/i.test(className + ' ' + id) ||
           el.hasAttribute('data-step') ||
           el.hasAttribute('data-page') ||
           el.getAttribute('role') === 'tabpanel' ||
           el.hasAttribute('data-active');
  }

  /**
   * Handle when a step container becomes active (CSS class detection).
   * Called when we detect an element gained the 'active' class.
   */
  private handleStepBecameActive(el: Element): void {
    const now = Date.now();
    const debounce = this.config.visibilityChangeDebounce || 150;

    // Debounce rapid toggles
    if (now - this.lastAutoSnapshot < debounce) {
      return;
    }

    // Verify we should capture
    if (!this.config.continueOnNavigation) {
      this.log(`[VISIBILITY_DETECT] Would capture snapshot but continueOnNavigation is disabled`);
      return;
    }

    if (!this.isRecording || !this.sessionKey) {
      return;
    }

    this.lastAutoSnapshot = now;
    this.log(`[VISIBILITY_DETECT] 📸 Step became active: ${this.describeElement(el)}`);

    // Capture snapshot for the newly active step
    this.queueNavigationSnapshot('manual', window.location.href, window.location.href);
  }

  // ==========================================
  // URL PATH POLLING (Fix 2 for PLL)
  // Catches pushState events that might be missed by interception
  // ==========================================

  /**
   * Initialize URL path polling as a fallback detection mechanism.
   * Some frameworks (like Next.js) may bypass our history.pushState wrapper.
   */
  private initUrlPathPolling(): void {
    if (!this.config.autoDetectPageChanges) return;
    if (!this.config.continueOnNavigation) return;

    this.lastUrlPath = window.location.pathname;

    // Poll every 100ms for URL changes
    this.urlPollingInterval = setInterval(() => {
      if (!this.isRecording) return;

      const currentPath = window.location.pathname;
      if (currentPath !== this.lastUrlPath) {
        const previousPath = this.lastUrlPath;
        this.lastUrlPath = currentPath;

        this.log(`[PATH_POLL] 📸 URL path changed: ${previousPath} → ${currentPath}`);
        this.queueNavigationSnapshot('manual', window.location.href, window.location.href);
      }
    }, 100);

    this.log('[PATH_POLL] ✅ URL path polling started');
  }

  /**
   * Clean up URL path polling interval.
   */
  private destroyUrlPathPolling(): void {
    if (this.urlPollingInterval) {
      clearInterval(this.urlPollingInterval);
      this.urlPollingInterval = null;
    }
    this.lastUrlPath = '';
    this.log('[PATH_POLL] URL path polling stopped');
  }

  // ==========================================
  // CONTENT-BASED DETECTION (Spec Implementation)
  // Works without specific class names - monitors form content changes
  // ==========================================

  /**
   * Initialize content change detection for multi-step forms.
   * This approach monitors the entire form/main content area for changes,
   * without requiring specific class names or data attributes.
   */
  private initContentChangeDetection(): void {
    if (!this.config.autoDetectPageChanges) return;
    if (!this.config.continueOnNavigation) return;

    this.log('[CONTENT_DETECT] 🔍 Initializing content-based step detection');

    // Get initial content hash
    this.lastContentHash = this.getFormContentHash();
    this.log(`[CONTENT_DETECT] Initial content hash: ${this.lastContentHash.substring(0, 50)}...`);

    // Create MutationObserver for content changes
    this.contentObserver = new MutationObserver(() => {
      this.handleContentMutation();
    });

    // Observe body for childList changes (DOM additions/removals)
    this.contentObserver.observe(document.body, {
      childList: true,
      subtree: true,
      // Don't observe attributes or characterData - too noisy for content detection
    });

    this.log('[CONTENT_DETECT] ✅ Content observer started');
  }

  /**
   * Generate a hash representing the current form/page content.
   * Focuses on forms and main content areas to detect step changes.
   */
  private getFormContentHash(): string {
    // Priority: forms > main content areas > body
    const forms = document.querySelectorAll('form');
    const mainSelectors = 'main, [role="main"], .main-content, #main, .form-container, .step-container, .wizard-content, .application-form, [class*="form-content"], [class*="step-content"]';
    const main = document.querySelector(mainSelectors);

    let target: Element | null = null;

    if (forms.length > 0) {
      // Use the largest form by content size
      target = Array.from(forms).reduce((a, b) =>
        a.innerHTML.length > b.innerHTML.length ? a : b
      );
    } else if (main) {
      target = main;
    } else {
      // Fallback to body, but try to find a meaningful container
      const containers = document.querySelectorAll('[class*="container"], [class*="content"], [class*="wrapper"]');
      if (containers.length > 0) {
        target = Array.from(containers).reduce((a, b) =>
          a.innerHTML.length > b.innerHTML.length ? a : b
        );
      }
    }

    if (!target) return '';

    // Create hash from: content length + input count + first 200 chars of text
    const text = target.textContent || '';
    const html = target.innerHTML;
    const inputCount = target.querySelectorAll('input, select, textarea').length;
    const labelText = Array.from(target.querySelectorAll('label, h1, h2, h3, .title, .heading'))
      .map(el => el.textContent?.trim())
      .filter(Boolean)
      .join('|')
      .substring(0, 100);

    return `${html.length}-${inputCount}-${labelText}-${text.slice(0, 200).replace(/\s+/g, ' ').trim()}`;
  }

  /**
   * Handle DOM mutations - debounced to avoid excessive processing.
   */
  private handleContentMutation(): void {
    // Clear any pending check
    if (this.contentChangeDebounceTimer) {
      clearTimeout(this.contentChangeDebounceTimer);
    }

    // Use configurable debounce (default 300ms, but allow faster with config)
    const debounce = this.config.captureSnapshotDebounce || 300;

    this.contentChangeDebounceTimer = setTimeout(() => {
      this.checkForContentChange();
    }, debounce);
  }

  /**
   * Check if content has changed significantly enough to warrant a snapshot.
   * Uses a character difference threshold to filter out minor DOM changes.
   */
  private checkForContentChange(): void {
    if (!this.isRecording || !this.sessionKey) return;

    const currentHash = this.getFormContentHash();

    // Skip if hash unchanged
    if (!currentHash || currentHash === this.lastContentHash) {
      return;
    }

    // Calculate change magnitude from content length
    const lastLength = parseInt(this.lastContentHash.split('-')[0]) || 0;
    const currentLength = parseInt(currentHash.split('-')[0]) || 0;
    const lengthChange = Math.abs(currentLength - lastLength);

    // Also check if input count changed (strong signal of step change)
    const lastInputCount = parseInt(this.lastContentHash.split('-')[1]) || 0;
    const currentInputCount = parseInt(currentHash.split('-')[1]) || 0;
    const inputCountChange = Math.abs(currentInputCount - lastInputCount);

    // Check if labels/headers changed (strong signal of step change)
    const lastLabels = this.lastContentHash.split('-')[2] || '';
    const currentLabels = currentHash.split('-')[2] || '';
    const labelsChanged = lastLabels !== currentLabels;

    // Minimum thresholds for step change detection
    const MIN_LENGTH_CHANGE = 300;  // 300 chars difference
    const MIN_INPUT_CHANGE = 2;      // 2 inputs added/removed
    const SIGNIFICANT_INPUT_CHANGE = 3; // 3+ inputs is definitely a step change

    // Determine if this is a significant change (step transition)
    const isSignificantChange =
      lengthChange >= MIN_LENGTH_CHANGE ||
      inputCountChange >= SIGNIFICANT_INPUT_CHANGE ||
      (inputCountChange >= MIN_INPUT_CHANGE && labelsChanged) ||
      (labelsChanged && lengthChange >= 100);

    if (isSignificantChange) {
      this.log(`[CONTENT_DETECT] 📸 Step change detected!`);
      this.log(`[CONTENT_DETECT]   Length: ${lastLength} → ${currentLength} (${lengthChange > 0 ? '+' : ''}${currentLength - lastLength})`);
      this.log(`[CONTENT_DETECT]   Inputs: ${lastInputCount} → ${currentInputCount} (${inputCountChange > 0 ? '+' : ''}${currentInputCount - lastInputCount})`);
      this.log(`[CONTENT_DETECT]   Labels changed: ${labelsChanged}`);

      // Update hash BEFORE capturing to prevent duplicate captures
      this.lastContentHash = currentHash;

      // Queue the snapshot capture
      this.queueNavigationSnapshot('manual', window.location.href, window.location.href);
    } else {
      // Update hash for minor changes without capturing
      this.lastContentHash = currentHash;
    }
  }

  /**
   * Clean up content change detection observer.
   */
  private destroyContentChangeDetection(): void {
    if (this.contentObserver) {
      this.contentObserver.disconnect();
      this.contentObserver = null;
    }

    if (this.contentChangeDebounceTimer) {
      clearTimeout(this.contentChangeDebounceTimer);
      this.contentChangeDebounceTimer = null;
    }

    this.lastContentHash = '';
    this.log('[CONTENT_DETECT] Content observer destroyed');
  }

  private handleClick = (event: MouseEvent): void => {
    const target = event.target as Element;
    if (!target) return;

    this.addEvent('click', {
      x: event.clientX,
      y: event.clientY,
      pageX: event.pageX,
      pageY: event.pageY
    }, target);
  };

  /**
   * W9: the ONE place an `input` event payload is built. Every capture path
   * (input, focus, blur, tab, poll) must go through here so the client-side
   * hardstop/password defence applies uniformly. Returns false if skipped.
   */
  private emitInputEvent(el: Element, source?: string): boolean {
    const data = buildInputEventData(el as unknown as InputLike, {
      hardstop: (n) => this.isHardstopField(n),
      trackSensitiveData: !!this.config.trackSensitiveData,
      findLabel: (x) => this.findFieldLabel(x as unknown as HTMLElement),
    });
    if (!data) return false;
    this.addEvent('input', source ? { ...data, source } : data, el);
    return true;
  }

  private handleInput = (event: Event): void => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (!target) return;
    
    // Skip if recorder not active
    if (!this.isRecording || !this.sessionKey) {
      if (this.config.debug) this.log('[DEBUG] Skipping input - recorder not active');
      return;
    }
    
    // Enhanced debug logging for Nicepage forms
    if (this.config.debug && window.location.href.includes('60-20.com')) {
      this.log(`[DEBUG] handleInput triggered: ${event.type} on ${target.tagName} - name="${target.name}" id="${target.id}" class="${target.className}" value="${target.value}"`);
      
      // Log why event might be skipped
      if (!['INPUT', 'TEXTAREA'].includes(target.tagName)) {
        this.log(`[DEBUG] Skipping - not INPUT/TEXTAREA`);
      }
      if (!('value' in target)) {
        this.log(`[DEBUG] Skipping - no value property`);
      }
    }
    
    // Force capture for Nicepage forms even without traditional events
    if (window.location.href.includes('60-20.com') || window.location.href.includes('nicepage')) {
      this.log(`[NICEPAGE] Force capturing input: ${target.name || target.id}`);
      // Continue processing even if it might be a synthetic event
    }
    
    // Only handle actual input elements
    if (!['INPUT', 'TEXTAREA'].includes(target.tagName)) {
      return;
    }
    
    // Ensure it's a form field with a value property
    if (!('value' in target)) {
      return;
    }
    
    // Throttle input events per field
    const fieldKey = target.name || target.id || target.tagName;
    const now = Date.now();
    const lastEventTime = this.lastInputEventTime.get(fieldKey) || 0;
    
    if (now - lastEventTime < this.inputThrottleDelay) {
      if (this.config.debug && window.location.href.includes('60-20.com')) {
        this.log(`[DEBUG] Throttled - too soon after last event (${now - lastEventTime}ms < ${this.inputThrottleDelay}ms)`);
      }
      return; // Skip this event, too soon after last one
    }
    
    this.lastInputEventTime.set(fieldKey, now);

    // Validate regex pattern to prevent console errors
    if (target.tagName === 'INPUT') {
      this.validateInputPattern(target as HTMLInputElement);
    }

    // W9: send the raw value plus classification signals. The server-side
    // ingestion classifier is the sole authority on what persists in plaintext
    // (src/services/pii-ingestion-gate.ts). Hardstop and password values are
    // still masked here as defence in depth; password fields are skipped
    // entirely unless trackSensitiveData is on.
    this.emitInputEvent(target);
  };

  private validateInputPattern(input: HTMLInputElement): void {
    const pattern = input.getAttribute('pattern');
    if (!pattern) return;

    try {
      // Test if the pattern is a valid regex
      new RegExp(pattern);
    } catch (error) {
      this.log(`Invalid regex pattern detected and sanitized: ${pattern}`);
      
      // Remove the invalid pattern to prevent browser validation errors
      input.removeAttribute('pattern');
      
      // Optionally add a basic phone pattern if it looks like a phone field
      if (input.type === 'tel' || input.name?.toLowerCase().includes('phone')) {
        input.setAttribute('pattern', '[0-9\\s\\-\\(\\)\\+]{10,}');
        this.log('Applied fallback phone pattern');
      }
    }
  }

  private handleFocus = (event: Event): void => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (!target) return;
    
    // Only handle actual input elements
    if (!['INPUT', 'TEXTAREA'].includes(target.tagName)) {
      return;
    }
    
    // Skip certain input types
    if (target.type === 'submit' || target.type === 'button' || target.type === 'reset') {
      return;
    }
    
    // Track currently focused element
    this.currentlyFocusedElement = target;
    
    if (this.config.debug) {
      this.log(`[FOCUS] Field focused: ${target.name || target.id || 'unknown'}`);
    }
    
    // If the field has a value when focused, capture it
    // This helps with pre-filled forms or forms that don't trigger input events
    if (target.value && target.value.trim() !== '') {
      this.emitInputEvent(target, 'focus');
    }
  };

  private handleBlur = (event: Event): void => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (!target) return;
    
    // Only handle actual input elements
    if (!['INPUT', 'TEXTAREA'].includes(target.tagName)) {
      return;
    }
    
    // Skip certain input types
    if (target.type === 'submit' || target.type === 'button' || target.type === 'reset') {
      return;
    }
    
    if (this.config.debug) {
      this.log(`[BLUR] Field blurred: ${target.name || target.id || 'unknown'} value="${target.value}"`);
    }
    
    // Capture the value when leaving the field
    if (target.value) {
      const key = target.name || target.id || target.getAttribute('data-ub-id') || '';
      
      // Only capture if value changed or not previously captured
      if (target.value !== this.capturedInputValues.get(key)) {
        this.capturedInputValues.set(key, target.value);

        this.emitInputEvent(target, 'blur');
        
        if (this.config.debug) {
          this.log(`[BLUR] Captured value on blur: ${key} = ${target.value}`);
        }
      }
    }
    
    // Clear focused element
    if (this.currentlyFocusedElement === target) {
      this.currentlyFocusedElement = null;
    }
  };

  private handleKeyDown = (event: KeyboardEvent): void => {
    // Only track Tab key
    if (event.key !== 'Tab') return;
    
    // If we have a focused element, capture its value before tab navigation
    if (this.currentlyFocusedElement && 
        ['INPUT', 'TEXTAREA'].includes(this.currentlyFocusedElement.tagName)) {
      
      const element = this.currentlyFocusedElement as HTMLInputElement | HTMLTextAreaElement;
      
      if (this.config.debug) {
        this.log(`[TAB] Tab pressed, capturing value from: ${element.name || element.id || 'unknown'}`);
      }
      
      // Capture value if exists
      if (element.value) {
        const key = element.name || element.id || element.getAttribute('data-ub-id') || '';
        
        if (element.value !== this.capturedInputValues.get(key)) {
          this.capturedInputValues.set(key, element.value);

          this.emitInputEvent(element, 'tab');
        }
      }
    }
  };

  private handleChange = (event: Event): void => {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    if (!target) return;

    if (target.tagName === 'SELECT' || target.type === 'checkbox' || target.type === 'radio') {
      // Privacy: mask change event values — actual values only sent via form submission endpoint
      const maskedValue = (target.type === 'checkbox' || target.type === 'radio')
        ? target.value  // checkbox/radio values are structural (option identifiers), not PII
        : '*'.repeat(target.value.length);

      this.addEvent('change', {
        value: maskedValue,
        checked: (target as HTMLInputElement).checked,
        type: target.type,
        name: target.name
      }, target);
    }
  };

  private handleScroll = (): void => {
    this.addEvent('scroll', {
      top: window.pageYOffset || document.documentElement.scrollTop,
      left: window.pageXOffset || document.documentElement.scrollLeft
    });
  };

  private handleMouseMove = (event: MouseEvent): void => {
    this.addEvent('mouse', {
      x: event.clientX,
      y: event.clientY
    });
  };

  private handleResize = (): void => {
    this.addEvent('size', {
      width: window.innerWidth,
      height: window.innerHeight
    });
  };

  private handleFormSubmit = (event: SubmitEvent): void => {
    const form = event.target as HTMLFormElement;
    if (!form) return;

    // Update lastSubmissionTime for deduplication with virtual submissions
    this.lastSubmissionTime = Date.now();

    // Debug logging for Nicepage forms
    if (this.config.debug && window.location.href.includes('60-20.com')) {
      this.log(`[DEBUG] Form submit event triggered`);
      this.log(`[DEBUG] Form has data-ub-id: ${form.getAttribute('data-ub-id')}`);
      this.log(`[DEBUG] Form action: ${form.action}`);
      this.log(`[DEBUG] Form ID: ${form.id}`);
      this.log(`[DEBUG] Form class: ${form.className}`);
    }

    // Get form identifier
    const ubId = form.getAttribute('data-ub-id');
    if (!ubId) {
      if (this.config.debug && window.location.href.includes('60-20.com')) {
        this.log(`[DEBUG] WARNING: Form missing data-ub-id attribute, submission not tracked!`);
      }
      return;
    }

    this.log('Form submission detected:', ubId);

    // Idle-ended: this.sessionKey is a stale/dead key, so posting the
    // submission marker below (or the recentCaptures bookkeeping, which
    // exists to dedupe against a virtual submission on the same live
    // session) would be writing against a session that's gone. Delegate
    // straight to captureFormSubmission(), which stashes this submission and
    // triggers the resume — see finding 8.
    if (this.isIdleEnded) {
      this.captureFormSubmission(form, parseInt(ubId, 10));
      return;
    }

    // Mark this form as recently captured to prevent duplicate virtual submission
    const formKey = ubId || form.id || form.className || 'form';
    this.recentCaptures.add(`${formKey}-${Date.now()}`);
    setTimeout(() => {
      // Clean up old captures
      for (const key of this.recentCaptures) {
        if (key.startsWith(formKey)) {
          this.recentCaptures.delete(key);
        }
      }
    }, 3000);

    // Log current form state for debugging
    const filledFields = Array.from(form.elements)
      .filter(el => (el as any).value && (el as any).value.trim() !== '')
      .length;
    this.log(`Form has ${filledFields} filled fields at submission time`);

    // Track form submission
    // Note: sendBeacon doesn't support custom headers, so we need to use fetch with keepalive
    const headers: any = {
      'Content-Type': 'application/json'
    };
    
    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey;
    }
    
    fetch(this.buildApiUrl(`/ub/pages/${this.sessionKey}/forms/${ubId}/submit`), { 
      method: 'POST',
      headers,
      body: JSON.stringify({}), // Empty body as the endpoint doesn't require data
      keepalive: true
    }).catch(error => {
      this.error('Failed to track form submission:', error);
    });

    // Capture and send complete form data to new endpoint
    if (this.config.captureFormSubmissions !== false) {
      this.captureFormSubmission(form, parseInt(ubId));
    }

    // Only stop recording if explicitly configured to do so
    if (this.config.stopOnFormSubmit === true) {
      // Prevent stopping too early - ensure minimum recording time
      const recordingDuration = Date.now() - this.startTime;
      const minRecordingTime = 5000; // 5 seconds minimum
      
      if (recordingDuration < minRecordingTime) {
        this.log(`Not stopping yet - only recorded for ${recordingDuration}ms (minimum: ${minRecordingTime}ms)`);
        return;
      }
      
      this.log('Stopping recording after form submission (stopOnFormSubmit=true)');
      // Use setTimeout to ensure the form submission event is captured
      setTimeout(() => {
        this.stop();
      }, 100);
    }
  };

  /**
   * Check if a field is a hardstop field (value must never be stored or transmitted).
   * Covers SSN, DOB, banking, payment cards, govt IDs, passwords/credentials.
   */
  private isHardstopField(fieldName: string): boolean {
    const name = fieldName.toLowerCase();
    const hardstopPatterns = [
      'password', 'passwd', 'pass', 'pwd', 'secret',
      'ssn', 'social_security', 'social-security',
      'dob', 'date_of_birth', 'date-of-birth', 'birthdate', 'birthday',
      'credit_card', 'credit-card', 'card_number', 'card-number', 'cc_number', 'pan',
      'cvv', 'cvc', 'cvv2', 'csc', 'security_code', 'security-code',
      'routing_number', 'routing-number', 'account_number', 'account-number',
      'bank_account', 'bank-account', 'iban', 'swift',
      'drivers_license', 'drivers-license', 'dl_number', 'dl-number', 'license_number',
      'passport', 'passport_number', 'passport-number',
      'ein', 'tax_id', 'tax-id', 'tin', 'itin',
      'pin', 'access_code', 'access-code', 'security_answer', 'security-answer',
    ];
    return hardstopPatterns.some(pattern => name.includes(pattern));
  }

  public captureFormSubmission(form: HTMLFormElement, formUbId: number): void {
    // A form submission is unambiguous user activity — wake the idle timer.
    this.idleTimer?.activity();
    if (this.isIdleEnded) {
      // Session is idle-ended (stale key, resume may still be in flight) —
      // don't write this submission against a dead/nonexistent session.
      // Unlike other activity, a submission is too valuable to just drop:
      // stash it (latest wins) so resumeFromIdle() can replay it against the
      // fresh key once the resume completes, and kick off (or retry) that
      // resume now.
      this.pendingIdleSubmission = { form, formUbId };
      this.scheduleIdleResume();
      return;
    }

    try {
      this.log(`Capturing form submission for form ${formUbId}`);
      
      // Enhanced debug logging for Nicepage forms
      if (this.config.debug && window.location.href.includes('60-20.com')) {
        this.log(`[DEBUG] Nicepage form submission detected`);
        this.log(`[DEBUG] Form action: ${form.action}`);
        this.log(`[DEBUG] Form method: ${form.method}`);
        this.log(`[DEBUG] Form class: ${form.className}`);
        this.log(`[DEBUG] Form elements count: ${form.elements.length}`);
      }
      
      // First, capture all current input values as a fallback
      // This ensures we get data even if input events weren't captured
      const fallbackData: Record<string, string> = {};
      const allInputs = form.querySelectorAll('input, textarea, select');
      
      if (this.config.debug && window.location.href.includes('60-20.com')) {
        this.log(`[DEBUG] Found ${allInputs.length} input/textarea/select elements`);
      }
      
      allInputs.forEach((element, index) => {
        const input = element as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        
        // Debug log ALL inputs for Nicepage
        if (this.config.debug && window.location.href.includes('60-20.com')) {
          this.log(`[DEBUG] Input[${index}]: tagName="${input.tagName}" type="${input.type}" name="${input.name}" id="${input.id}" value="${input.value}" class="${input.className}"`);
        }
        
        // Skip certain types
        if (input.type === 'submit' || input.type === 'button' || input.type === 'reset') return;
        if (!this.config.trackSensitiveData && input.type === 'password') return;
        
        // Get the value
        let value = '';
        if (input.type === 'checkbox') {
          value = (input as HTMLInputElement).checked ? 'checked' : '';
        } else if (input.type === 'radio') {
          value = (input as HTMLInputElement).checked ? input.value : '';
        } else {
          value = input.value || '';
        }
        
        if (value) {
          const fieldName = input.name || input.id || `${input.tagName.toLowerCase()}_${Array.from(allInputs).indexOf(element)}`;
          fallbackData[fieldName] = value;
          
          // Debug logging for Nicepage forms
          if (this.config.debug && window.location.href.includes('60-20.com')) {
            this.log(`[DEBUG] Captured field: name="${input.name}" id="${input.id}" fieldName="${fieldName}" value="${value}" type="${input.type}"`);
          }
          
          // Also emit an input event so the final value is recorded. Goes
          // through the same builder as every other capture path: full
          // classification signals, raw value (the server-side ingestion gate
          // masks anything it can't confirm), and the builder's own
          // hardstop / password handling. Pre-W9 this path sent a pre-masked
          // value, which the classifier could never confirm (spec 2026-09-12 §A).
          this.emitInputEvent(input, 'form-submission-fallback');
        } else if (this.config.debug && window.location.href.includes('60-20.com')) {
          // Log fields without values too
          this.log(`[DEBUG] Empty field: name="${input.name}" id="${input.id}" type="${input.type}"`);
        }
      });
      
      const formData = new FormData(form);
      const fields: any[] = [];
      const rawData: Record<string, any> = {};

      // Extract form fields
      formData.forEach((value, name) => {
        // Hardstop fields: replace value with [REDACTED] — never transmit actual value
        if (this.isHardstopField(name as string)) {
          this.log(`Redacting hardstop field: ${name}`);
          fields.push({
            name: name,
            value: '[REDACTED]',
            type: 'text',
            label: name,
            required: false
          });
          rawData[name] = '[REDACTED]';
          return;
        }

        // Get the form element
        const element = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        
        // Try to get the label text for display
        let label = name; // Default to field name
        let isConsent = false;
        if (element) {
          // Check for aria-label first
          if (element.getAttribute('aria-label')) {
            label = element.getAttribute('aria-label')!;
          }
          // Try to find associated label element
          else if (element.id) {
            const labelElement = form.querySelector(`label[for="${element.id}"]`);
            if (labelElement) {
              label = labelElement.textContent?.trim() || name;
            }
          }

          // Detect consent fields via CSS class on label/wrapper or name/id keywords
          isConsent = this.isConsentField(element);
        }

        fields.push({
          name: name,  // Technical field name (e.g., "phone")
          value: value,
          type: element?.type || 'text',
          label: label, // Human-readable label (e.g., "Phone" or "Phone Number")
          required: element?.required || false,
          ...(isConsent ? { isConsent: true } : {}),
        });

        rawData[name] = value;
      });

      // If FormData was empty but we have fallback data, use it
      if (fields.length === 0 && Object.keys(fallbackData).length > 0) {
        this.log('FormData was empty, using fallback data capture');
        Object.entries(fallbackData).forEach(([name, value]) => {
          if (this.isHardstopField(name)) {
            fields.push({
              name: name,
              value: '[REDACTED]',
              type: 'text',
              label: name,
              required: false
            });
            rawData[name] = '[REDACTED]';
          } else {
            fields.push({
              name: name,
              value: value,
              type: 'text',
              label: name,
              required: false
            });
            rawData[name] = value;
          }
        });
      }

      // Get form metadata
      const formName = form.getAttribute('name') || form.getAttribute('id') || `form-${formUbId}`;
      
      // Prepare submission data
      const submissionData = {
        sessionKey: this.sessionKey!,
        projectId: this.config.projectId,
        formId: formUbId.toString(),
        formName: formName,
        fields: fields,
        rawData: rawData,
        url: window.location.href,
        visitor: {
          id: this.config.visitorId || 0,
          key: this.config.visitorKey || '',
          ip: '', // Will be set by server
          userAgent: navigator.userAgent
        },
        source: document.referrer ? new URL(document.referrer).hostname : 'direct',
        referrer: document.referrer,
        metadata: {
          formAction: form.action,
          formMethod: form.method,
          pageTitle: document.title
        }
      };


      // Capture TCPA consent if a consent checkbox is present
      this.captureTcpaConsent(form, formUbId);

      // Send form submission data
      const headers: any = {
        'Content-Type': 'application/json'
      };
      if (this.config.apiKey) {
        headers['X-API-Key'] = this.config.apiKey;
      }

      const apiUrl = this.buildApiUrl(`/projects/${this.config.projectId}/form-submissions`);
      // Debug logging removed
      // Debug logging removed

      // Note: We don't capture form submission as a regular event because
      // 'formSubmission' is not a valid event type in the API schema.
      // Form submissions are tracked via the dedicated /form-submissions endpoint.

      fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(submissionData),
        keepalive: true
      }).then(async response => {
        // Debug logging removed
        
        if (response.ok) {
          this.log('✅ Form submission data captured successfully');
          // Parse response but don't need to use it
          await response.json().catch(() => null);
          
          // Stop recording after successful form submission if explicitly configured
          // NOTE: Only stop if stopOnFormSubmit is explicitly true (not undefined)
          // This allows multi-step forms to continue recording through all steps
          if (this.config.stopOnFormSubmit === true) {
            this.log('Stopping recording after form submission (stopOnFormSubmit=true)');
            this.stop();
          }
        } else {
          const errorText = await response.text().catch(() => 'Unable to read error response');
          this.error(`❌ Form submission failed with status ${response.status}: ${errorText}`);
          // Debug logging removed
        }
      }).catch(error => {
        this.error('❌ Failed to capture form submission data:', error);
        // Debug logging removed
      });
    } catch (error) {
      this.error('❌ Error capturing form submission:', error);
      // Debug logging removed
    }
  }

  /**
   * Check if a form element is a consent-related field.
   * Inspects CSS classes on the label/wrapper, the element's name/id, and label text.
   */
  private isConsentField(element: Element): boolean {
    const CONSENT_CLASS_KEYWORDS = /consent|tcpa|opt[_-]?in|gdpr/i;
    const CONSENT_TEXT_KEYWORDS = /consent|agree|tcpa|authorize|opt[_-]?in|permission|terms|conditions/i;

    // Check CSS classes on ancestor labels/wrappers (e.g. Gravity Forms gfield_consent_label)
    const closestLabel = element.closest('.gfield_consent_label, .gfield_consent, [class*="consent"], [class*="tcpa"]');
    if (closestLabel) return true;

    // Check the element's associated label for consent classes
    if ((element as HTMLInputElement).id) {
      const labelFor = document.querySelector(`label[for="${(element as HTMLInputElement).id}"]`);
      if (labelFor && CONSENT_CLASS_KEYWORDS.test(labelFor.className)) return true;
    }

    // Check parent label classes
    const parentLabel = element.closest('label');
    if (parentLabel && CONSENT_CLASS_KEYWORDS.test(parentLabel.className)) return true;

    // Check element name and id attributes
    const name = (element as HTMLInputElement).name || '';
    const id = (element as HTMLInputElement).id || '';
    if (CONSENT_TEXT_KEYWORDS.test(name) || CONSENT_TEXT_KEYWORDS.test(id)) return true;

    return false;
  }

  /**
   * Detect and capture TCPA consent checkbox state from a form.
   * Scans checkboxes for consent-related keywords in labels/text, CSS classes,
   * and element name/id attributes. Captures ALL matching consent checkboxes.
   */
  private captureTcpaConsent(form: HTMLFormElement, formUbId: number): void {
    if (!this.sessionKey) return;

    // One submission, one consent record — see recentTcpaCaptures. Only a scan
    // that actually FINDS a consent checkbox stamps the window (stampDedupe()
    // below, called just before the first POST). A scan that finds nothing must
    // not consume it: multi-page Gravity Forms reuse one data-ub-id across
    // steps and isSubmitButton() counts "Next"/"Continue" as a submit, so
    // stamping on an empty scan would silently swallow the real consent capture
    // on the final step seconds later.
    const dedupeKey = `${this.sessionKey}-${formUbId}`;
    const nowMs = Date.now();
    const lastCapture = this.recentTcpaCaptures.get(dedupeKey);
    if (lastCapture !== undefined && nowMs - lastCapture < ValidiformRecorder.TCPA_DEDUPE_WINDOW_MS) {
      this.log('Skipping TCPA consent capture - already captured for this submission:', dedupeKey);
      return;
    }
    let dedupeStamped = false;
    const stampDedupe = (): void => {
      if (dedupeStamped) return;  // one stamp per call, however many boxes match
      dedupeStamped = true;
      this.recentTcpaCaptures.set(dedupeKey, nowMs);
      setTimeout(() => this.recentTcpaCaptures.delete(dedupeKey), ValidiformRecorder.TCPA_DEDUPE_WINDOW_MS);
    };

    const CONSENT_KEYWORDS = /consent|agree|tcpa|authorize|opt[_-]?in|permission|terms|conditions/i;

    try {
      const checkboxes = form.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
      for (const checkbox of checkboxes) {
        let labelText = '';
        let isConsentCheckbox = false;

        // Strategy 1: Check for <label for="id">
        if (checkbox.id) {
          const label = form.querySelector(`label[for="${checkbox.id}"]`);
          if (label) {
            labelText = label.textContent?.trim() || '';
            // Also check label CSS classes for consent keywords
            if (/consent|tcpa|opt[_-]?in|gdpr/i.test(label.className)) {
              isConsentCheckbox = true;
            }
          }
        }

        // Strategy 2: Check parent label
        if (!labelText) {
          const parentLabel = checkbox.closest('label');
          if (parentLabel) {
            labelText = parentLabel.textContent?.trim() || '';
            if (/consent|tcpa|opt[_-]?in|gdpr/i.test(parentLabel.className)) {
              isConsentCheckbox = true;
            }
          }
        }

        // Strategy 3: Check adjacent sibling text
        if (!labelText && checkbox.nextSibling) {
          labelText = checkbox.nextSibling.textContent?.trim() || '';
        }

        // Strategy 4: Check ancestor wrappers for consent CSS classes (e.g. Gravity Forms gfield_consent_label)
        if (!isConsentCheckbox) {
          const consentWrapper = checkbox.closest('.gfield_consent_label, .gfield_consent, [class*="consent"], [class*="tcpa"]');
          if (consentWrapper) {
            isConsentCheckbox = true;
            // If we still have no label text, grab it from the wrapper
            if (!labelText) {
              labelText = consentWrapper.textContent?.trim() || '';
            }
          }
        }

        // Strategy 5: Check checkbox name and id for consent keywords
        if (!isConsentCheckbox) {
          const nameAttr = checkbox.name || '';
          const idAttr = checkbox.id || '';
          if (CONSENT_KEYWORDS.test(nameAttr) || CONSENT_KEYWORDS.test(idAttr)) {
            isConsentCheckbox = true;
          }
        }

        // Final check: keyword match on label text
        if (!isConsentCheckbox && CONSENT_KEYWORDS.test(labelText)) {
          isConsentCheckbox = true;
        }

        if (!isConsentCheckbox) continue;

        // Found a consent checkbox — this submission is now on the record, so
        // claim the dedupe window before sending.
        stampDedupe();

        const consentData = {
          checked: checkbox.checked,
          timestamp: Date.now(),
          consentText: labelText.substring(0, 1000), // Cap at 1000 chars
          pageUrl: window.location.href,
          formId: formUbId.toString(),
          ipAddress: '',     // Set by server
          userAgent: navigator.userAgent,
        };

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (this.config.apiKey) {
          headers['X-API-Key'] = this.config.apiKey;
        }

        fetch(this.buildApiUrl(`/sessions/${this.sessionKey}/tcpa`), {
          method: 'POST',
          headers,
          body: JSON.stringify(consentData),
          keepalive: true,
        }).then(response => {
          if (response.ok) {
            this.log('✅ TCPA consent captured');
          } else {
            this.error(`❌ TCPA consent capture failed: ${response.status}`);
          }
        }).catch(error => {
          this.error('❌ TCPA consent capture error:', error);
        });

        // Continue iterating — capture ALL consent checkboxes, not just the first
      }
    } catch (error) {
      this.error('❌ Error detecting TCPA consent:', error);
    }
  }

  private setupMutationObserver(): void {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        // Skip mutations on debug console or recorder UI elements
        const target = mutation.target as Element;
        if (target && (target.id === 'console' || target.classList?.contains('debug-line') || 
            target.closest('#debugConsole'))) {
          return;
        }
        
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;

              // Skip debug/console elements
              if (element.id === 'console' || element.classList?.contains('debug-line')) {
                return;
              }

              // Only assign IDs to the new element and its children
              this.assignElementIdsToSubtree(element);

              // If a form was added, ensure it has submit tracking
              if (element.tagName === 'FORM' || element.querySelector('form')) {
                this.setupFormButtonTracking();
              }

              this.addEvent('add', {
                html: element.outerHTML.substring(0, 200) // Limit HTML size
              }, element.parentElement || undefined);
            }
          });

          mutation.removedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;

              // Skip debug/console elements
              if (element.id === 'console' || element.classList?.contains('debug-line')) {
                return;
              }

              this.addEvent('remove', {
                html: element.outerHTML.substring(0, 200) // Limit HTML size
              }, element.parentElement || undefined);
            }
          });

          // AUTO-DETECT: Track mutations for burst detection (SPA step changes)
          if (this.config.autoDetectPageChanges) {
            const mode = this.config.pageChangeDetectionMode || 'both';
            if (mode === 'mutation' || mode === 'both') {
              // Find form ancestor for mutation tracking
              const formAncestor = (mutation.target as Element).closest(
                'form, [class*="form"], [class*="wizard"], [class*="step"], [class*="multi"]'
              );
              if (formAncestor) {
                this.trackMutationBurst(formAncestor, mutation);
              }
            }
          }
        } else if (mutation.type === 'attributes') {
          const element = mutation.target as Element;
          const attrName = mutation.attributeName;

          // Skip data-ub-id changes, internal tracking attrs, and debug elements
          if (attrName === 'data-ub-id' || attrName === 'data-ub-was-visible' ||
              attrName === 'data-ub-was-active' ||
              element.id === 'console' || element.classList?.contains('debug-line')) {
            return;
          }

          // AUTO-DETECT: Check for visibility changes on step containers (existing logic)
          if (this.config.autoDetectPageChanges && this.stepContainers.has(element)) {
            const mode = this.config.pageChangeDetectionMode || 'both';
            if (mode === 'visibility' || mode === 'both') {
              // Check visibility-related attributes
              if (attrName === 'class' || attrName === 'style' ||
                  attrName === 'hidden' || attrName === 'aria-hidden' ||
                  attrName === 'data-active' || attrName === 'data-step') {
                this.checkVisibilityChange(element, attrName!);
              }
            }
          }

          // FIX 1: CSS visibility class detection for PLL pattern
          // Detect when ANY element matching step container patterns gains 'active' class
          // This catches PLL's pattern where step-section divs toggle active/inactive
          if (attrName === 'class' && this.config.autoDetectPageChanges && this.config.continueOnNavigation) {
            if (this.isLikelyStepContainer(element)) {
              const hasActive = element.classList.contains('active') ||
                               element.classList.contains('current') ||
                               element.classList.contains('show') ||
                               element.classList.contains('visible');
              const wasActive = element.getAttribute('data-ub-was-active') === 'true';

              if (hasActive && !wasActive) {
                // Step just became active - trigger snapshot
                this.handleStepBecameActive(element);
              }

              // Update tracking attribute
              element.setAttribute('data-ub-was-active', String(hasActive));
            }
          }

          // Skip static form metadata — already captured in snapshot HTML
          if (attrName && ValidiformRecorder.ATTR_BLOCKLIST.has(attrName)) {
            return;
          }

          // Throttle style attribute changes to 200ms per element (collapses animation frames)
          if (attrName === 'style') {
            const elPath = this.getElementPath(element);
            const now = Date.now();
            const lastTime = this.styleThrottleMap.get(elPath) || 0;
            if (now - lastTime < ValidiformRecorder.STYLE_THROTTLE_MS) {
              return;
            }
            this.styleThrottleMap.set(elPath, now);
          }

          this.addEvent('attribute', {
            name: attrName,
            value: element.getAttribute(attrName!)
          }, element);
        } else if (mutation.type === 'characterData') {
          const element = mutation.target.parentElement;
          
          // Skip text changes in debug console
          if (element && !element.closest('#console, #debugConsole')) {
            this.addEvent('text', {
              value: mutation.target.textContent?.substring(0, 100) // Limit text size
            }, element);
          }
        }
      });
    });

    this.observer.observe(document.documentElement, {
      childList: true,
      attributes: true,
      characterData: true,
      subtree: true,
      attributeOldValue: true,
      characterDataOldValue: true
    });
  }

  private stopMutationObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flushEvents();
    }, this.config.flushInterval);
  }

  private stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private startInputPolling(): void {
    // Poll more frequently for better capture, especially during tab navigation
    this.inputPollTimer = window.setInterval(() => {
      if (!this.isRecording || !this.sessionKey) return;
      
      // Check if focus has changed
      const currentFocus = document.activeElement as HTMLInputElement | HTMLTextAreaElement;
      if (currentFocus && currentFocus !== this.currentlyFocusedElement &&
          ['INPUT', 'TEXTAREA'].includes(currentFocus.tagName)) {
        
        // Focus changed, capture previous element's value if any
        if (this.currentlyFocusedElement && 
            ['INPUT', 'TEXTAREA'].includes(this.currentlyFocusedElement.tagName)) {
          const prevElement = this.currentlyFocusedElement as HTMLInputElement | HTMLTextAreaElement;
          if (prevElement.value) {
            const key = prevElement.name || prevElement.id || prevElement.getAttribute('data-ub-id') || '';
            if (prevElement.value !== this.capturedInputValues.get(key)) {
              this.capturedInputValues.set(key, prevElement.value);

              this.emitInputEvent(prevElement, 'poll-blur');
              
              if (this.config.debug) {
                this.log(`[POLL] Focus changed, captured previous field: ${key} = ${prevElement.value}`);
              }
            }
          }
        }
        
        // Update tracked element
        this.currentlyFocusedElement = currentFocus;
      }
      
      // Regular polling for all inputs
      const inputs = document.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea');
      
      inputs.forEach(input => {
        const element = input as HTMLInputElement | HTMLTextAreaElement;
        const value = element.value;
        const key = element.name || element.id || element.getAttribute('data-ub-id') || '';
        
        // Only capture if value changed
        if (value && value !== this.capturedInputValues.get(key)) {
          this.capturedInputValues.set(key, value);
          
          if (this.config.debug && window.location.href.includes('60-20.com')) {
            this.log(`[POLL] Captured input value: ${key} = ${value}`);
          }
          
          // Add event for this value
          this.emitInputEvent(element, 'poll');
        }
      });
    }, 500); // Poll every 500ms for better responsiveness
  }

  private stopInputPolling(): void {
    if (this.inputPollTimer) {
      clearInterval(this.inputPollTimer);
      this.inputPollTimer = null;
    }
    this.capturedInputValues.clear();
  }

  private async endSession(opts?: { reason?: 'idle' | 'aborted-resume'; durationOverride?: number }): Promise<void> {
    if (!this.sessionKey) return;

    try {
      const endpoint = this.buildApiUrl(`/ub/pages/${this.sessionKey}/end`);

      // Calculate duration as the max of:
      // 1. Time since session start (Date.now() - startTime)
      // 2. Last recorded event time (lastEventTime)
      // This ensures duration is accurate even if endSession is called early
      // (e.g., by beforeunload when user switches tabs but doesn't actually leave)
      // durationOverride bypasses this — used by the idle cutoff so the idle wait
      // itself doesn't get baked into the reported session duration, and by the
      // aborted-resume path (an orphaned session ended with no real activity).
      const calculatedDuration = Date.now() - this.startTime;
      const duration = opts?.durationOverride ?? Math.max(calculatedDuration, this.lastEventTime);

      const payload: { duration: number; endReason?: 'idle' | 'aborted-resume' } = { duration };
      if (opts?.reason) {
        payload.endReason = opts.reason;
      }

      this.log(`Ending session with duration: ${duration}ms (calculated: ${calculatedDuration}ms, lastEvent: ${this.lastEventTime}ms, reason: ${opts?.reason ?? 'default'})`);

      // Always use fetch when API key is required (sendBeacon can't send headers)
      const headers: any = {
        'Content-Type': 'application/json'
      };

      if (this.config.apiKey) {
        headers['X-API-Key'] = this.config.apiKey;
      }

      await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        keepalive: true // Keep request alive even if page is unloading
      });

      this.log('Session ended successfully');

      if (this.config.onSessionEnd) {
        this.config.onSessionEnd();
      }
    } catch (error) {
      // Don't log errors during page unload as they're expected
      if (document.visibilityState !== 'hidden') {
        this.error('Failed to end session:', error);
      }
    }
  }

  private getOrCreateVisitorId(): number {
    const stored = localStorage.getItem('validiform_visitor_id');
    if (stored) {
      return parseInt(stored, 10);
    }
    const id = Math.floor(Math.random() * 1000000) + 1;
    localStorage.setItem('validiform_visitor_id', id.toString());
    return id;
  }

  private getOrCreateVisitorKey(): string {
    // Check both old and new storage keys for compatibility
    const stored = localStorage.getItem('ub.visitorKey') || localStorage.getItem('validiform_visitor_key');
    if (stored) {
      return stored;
    }
    const key = Date.now().toString(36) + Math.random().toString(36).substring(2);
    localStorage.setItem('ub.visitorKey', key);
    localStorage.setItem('validiform_visitor_key', key); // Keep old key for compatibility
    return key;
  }

  private async captureOptimizedHtml(): Promise<string> {
    const MAX_PAYLOAD_SIZE = 4 * 1024 * 1024; // 4MB limit - matches server limit with buffer
    const MAX_CSS_SIZE = 2 * 1024 * 1024; // 2MB limit for CSS
    
    try {
      // Start with basic HTML
      let html = document.documentElement.outerHTML;
      
      // Apply CSS proxy to cross-origin stylesheets
      html = this.processCssLinksForProxy(html);
      
      // Check if basic HTML is already too large
      if (this.getByteSize(html) > MAX_PAYLOAD_SIZE) {
        this.log('HTML too large, using stripped version');
        return this.stripLargeElements(html);
      }
      
      // Try to add enhanced CSS capture (from main branch)
      try {
        const enhancedHtml = await this.captureHtmlWithCss();
        
        // Try minification first
        const minifiedHtml = this.minifyHtml(enhancedHtml);
        
        // CRITICAL FIX: Apply CSS proxy processing to enhanced HTML
        // The captureHtmlWithCss() method preserves original <link> tags which will fail on playback
        // We must convert ALL external CSS links to use the proxy for cross-origin playback compatibility
        const processedEnhancedHtml = this.processCssLinksForProxy(enhancedHtml);
        const processedMinifiedHtml = this.processCssLinksForProxy(minifiedHtml);

        // Check if minified HTML with CSS is within limits
        if (this.getByteSize(processedMinifiedHtml) <= MAX_PAYLOAD_SIZE) {
          this.log('Using minified HTML with enhanced CSS capture (with proxy URLs)');
          return processedMinifiedHtml;
        } else if (this.getByteSize(processedEnhancedHtml) <= MAX_PAYLOAD_SIZE) {
          // If minification didn't help enough, try unminified
          this.log('Using HTML with enhanced CSS capture (unminified, with proxy URLs)');
          return processedEnhancedHtml;
        } else {
          this.log('Enhanced HTML too large even after minification, falling back to basic HTML with critical CSS');
        }
      } catch (cssError) {
        this.log('Enhanced CSS capture failed, falling back to critical CSS extraction');
      }
      
      // Fallback: Try to add critical CSS using simple extraction
      const criticalCss = this.extractCriticalCss();
      if (criticalCss && criticalCss.length < MAX_CSS_SIZE) {
        const htmlWithCss = this.injectCssIntoHtml(html, criticalCss);
        const minifiedHtmlWithCss = this.minifyHtml(htmlWithCss);
        
        // Check if adding CSS exceeds limit
        if (this.getByteSize(minifiedHtmlWithCss) <= MAX_PAYLOAD_SIZE) {
          this.log('Using minified HTML with critical CSS');
          return minifiedHtmlWithCss;
        } else if (this.getByteSize(htmlWithCss) <= MAX_PAYLOAD_SIZE) {
          this.log('Using HTML with critical CSS (unminified)');
          return htmlWithCss;
        } else {
          this.log('HTML with CSS too large even after minification, using basic HTML');
        }
      }
      
      // Final fallback: minify basic HTML
      const minifiedBasicHtml = this.minifyHtml(html);
      if (this.getByteSize(minifiedBasicHtml) < this.getByteSize(html)) {
        this.log('Using minified basic HTML');
        return minifiedBasicHtml;
      }
      
      return html;
    } catch (error) {
      this.error('Failed to optimize HTML:', error);
      // Even on error, process CSS links for proxy
      const fallbackHtml = document.documentElement.outerHTML;
      return this.processCssLinksForProxy(fallbackHtml);
    }
  }

  private async captureHtmlWithCss(): Promise<string> {
    try {
      this.log(`🎨 [CSS_CAPTURE] Starting enhanced CSS capture...`);
      this.log(`🎨 [CSS_CAPTURE] Config: apiUrl=${this.config.apiUrl}, apiKey present=${!!this.config.apiKey}`);
      this.log(`🎨 [CSS_CAPTURE] Found ${document.styleSheets.length} stylesheets to process`);

      // Start with the current HTML
      const htmlClone = document.documentElement.cloneNode(true) as HTMLElement;

      // Collect all CSS from stylesheets
      const cssCollector: string[] = [];
      const externalStylesheets: string[] = [];

      // Process all stylesheets
      for (const sheet of Array.from(document.styleSheets)) {
        try {
          // Handle external stylesheets
          if (sheet.href) {
            if (this.isSameOrigin(sheet.href)) {
              // Same-origin stylesheet - try to extract rules
              try {
                if (sheet.cssRules) {
                  const rules = Array.from(sheet.cssRules).map(rule => rule.cssText).join('\n');
                  if (rules.trim()) {
                    cssCollector.push(`/* From: ${sheet.href} */\n${rules}`);
                  }
                }
              } catch (error) {
                // Even same-origin can fail due to CORS policy - try fetching it
                this.log(`Could not access same-origin stylesheet rules, trying fetch: ${sheet.href}`);
                try {
                  const cssText = await this.fetchExternalCSS(sheet.href);
                  if (cssText) {
                    cssCollector.push(`/* From: ${sheet.href} */\n${cssText}`);
                    this.log(`Successfully fetched same-origin CSS: ${sheet.href} (${cssText.length} chars)`);
                  } else {
                    externalStylesheets.push(sheet.href);
                  }
                } catch (fetchError) {
                  this.log(`Failed to fetch same-origin CSS: ${sheet.href}`, fetchError);
                  externalStylesheets.push(sheet.href);
                }
              }
            } else {
              // Cross-origin stylesheet - FETCH AND INLINE IT FOR BLUE HEADERS
              // Skip non-critical stylesheets to save space
              const skipPatterns = [
                'fonts.googleapis.com',
                'fonts.gstatic.com',
                'font-awesome',
                'fontawesome',
                'icon',
                'animate.css',
                'animation'
              ];
              
              const shouldSkip = skipPatterns.some(pattern => sheet.href?.toLowerCase().includes(pattern));
              
              if (shouldSkip) {
                this.log(`⏭️ Skipping non-critical stylesheet to save space: ${sheet.href}`);
                externalStylesheets.push(sheet.href);
              } else {
                this.log(`🎨 CSS INLINING v4.0: Fetching stylesheet for blue header preservation: ${sheet.href}`);
                try {
                  const cssText = await this.fetchExternalCSS(sheet.href);
                  if (cssText) {
                    // Filter out non-critical CSS to reduce size
                    const filteredCss = this.filterNonCriticalCss(cssText);
                    cssCollector.push(`/* From: ${sheet.href} */\n${filteredCss}`);
                    this.log(`✅ CSS INLINED: Successfully preserved styling from: ${sheet.href} (${cssText.length} chars -> ${filteredCss.length} chars after filtering)`);
                  } else {
                    externalStylesheets.push(sheet.href);
                  }
                } catch (error) {
                  this.log(`❌ CSS INLINE FAILED: ${sheet.href}`, error);
                  externalStylesheets.push(sheet.href);
                }
              }
            }
          } else {
            // Inline stylesheet
            try {
              if (sheet.cssRules) {
                const rules = Array.from(sheet.cssRules)
                  .map(rule => {
                    // Keep ALL CSS rules during capture - let the player handle problematic ones
                    // Only filter out extremely problematic rules that cause total layout breaks
                    const cssText = rule.cssText;
                    
                    // Only filter the most extreme cases (font-size > 5rem)
                    if (cssText.includes('font-size') && cssText.match(/font-size:\s*[5-9]\d*rem|font-size:\s*\d{2,}rem/)) {
                      this.log(`Filtering extreme font-size rule: ${cssText.substring(0, 100)}...`);
                      // Replace extreme font-sizes with 2rem max, but keep all other styling
                      return cssText.replace(/font-size:\s*([5-9]\d*|[1-9]\d{2,})rem/g, 'font-size: 2rem');
                    }
                    
                    return cssText;
                  })
                  .filter(rule => rule) // Remove empty rules
                  .join('\n');
                if (rules.trim()) {
                  cssCollector.push(`/* Inline styles */\n${rules}`);
                }
              }
            } catch (error) {
              this.log('Could not access inline stylesheet rules');
            }
          }
        } catch (error) {
          this.log(`Error processing stylesheet: ${sheet.href || 'inline'}`, error);
        }
      }
      
      // Capture WordPress-specific styles
      const wpStyles = this.captureWordPressStyles();
      if (wpStyles) {
        cssCollector.push(`/* WordPress Styles */\n${wpStyles}`);
      }
      
      // Capture computed styles for critical elements with enhanced form focus
      const criticalSelectors = ['form', 'input', 'button', 'select', 'textarea', 'label', 'body', 'html'];
      const computedStyles: string[] = [];
      
      // Enhanced form-specific CSS capture
      const enhancedFormCSS = this.captureEnhancedFormCSS();
      if (enhancedFormCSS) {
        computedStyles.push(enhancedFormCSS);
      }
      
      criticalSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach((element) => {
          const ubId = element.getAttribute('data-ub-id');
          if (ubId || selector === 'body' || selector === 'html') {
            const computed = window.getComputedStyle(element);
            const criticalProps = [
              'display', 'position', 'top', 'left', 'width', 'height',
              'margin', 'padding', 'border', 'background', 'background-color',
              'color', 'font-size', 'font-family', 'font-weight', 'text-align',
              'line-height', 'letter-spacing', 'text-decoration'
            ];
            
            const cssProps = criticalProps
              .map(prop => {
                const value = computed.getPropertyValue(prop);
                
                // Filter out extreme font-sizes that are likely desktop-only media queries
                if (prop === 'font-size' && (selector === 'body' || selector === 'html')) {
                  const fontSize = parseFloat(value);
                  const unit = value.replace(/[0-9.-]/g, '');
                  
                  // If font-size is unusually large (> 2rem or > 32px), use a reasonable default
                  if ((unit === 'rem' && fontSize > 2) || 
                      (unit === 'px' && fontSize > 32) ||
                      (unit === 'em' && fontSize > 2)) {
                    this.log(`Filtering extreme font-size: ${value} for ${selector}, using 1rem instead`);
                    return `${prop}: 1rem`;
                  }
                }
                
                return `${prop}: ${value}`;
              })
              .filter(prop => !prop.endsWith(': ') && !prop.endsWith(': initial') && !prop.endsWith(': normal'))
              .join('; ');
              
            if (cssProps) {
              if (ubId) {
                computedStyles.push(`[data-ub-id="${ubId}"] { ${cssProps} }`);
              } else {
                computedStyles.push(`${selector} { ${cssProps} }`);
              }
            }
          }
        });
      });
      
      // Find or create head element in clone
      let head = htmlClone.querySelector('head');
      if (!head) {
        head = document.createElement('head');
        htmlClone.insertBefore(head, htmlClone.firstChild);
      }
      
      // Add external stylesheet references - ALL use proxy since playback is on different domain
      externalStylesheets.forEach(href => {
        const linkElement = document.createElement('link');
        linkElement.rel = 'stylesheet';
        linkElement.type = 'text/css';

        // ALWAYS use CSS proxy for external stylesheets
        // Key insight: Recording might be on goodrep.media (same-origin for CSS)
        // but playback is ALWAYS on api.validiform.com (cross-origin for CSS)
        // So ALL external CSS links must use the proxy for playback to work
        const proxyUrl = this.buildApiUrl(`/proxy/css?url=${encodeURIComponent(href)}`);
        linkElement.href = proxyUrl;
        linkElement.setAttribute('data-original-href', href);
        const wasSameOrigin = this.isSameOrigin(href);
        this.log(`[CSS_PROXY] Converting ${wasSameOrigin ? 'same-origin' : 'cross-origin'} failed stylesheet to proxy: ${href}`);

        head!.appendChild(linkElement);
      });
      
      // Combine all captured CSS
      const allCss = [...cssCollector, ...computedStyles].join('\n');

      // Add embedded style tag with all CSS
      if (allCss.trim()) {
        const styleElement = document.createElement('style');
        styleElement.type = 'text/css';
        styleElement.textContent = allCss;
        head.appendChild(styleElement);
      }

      // Summary logging
      this.log(`🎨 [CSS_CAPTURE] ========== SUMMARY ==========`);
      this.log(`🎨 [CSS_CAPTURE] ✅ Inlined stylesheets: ${cssCollector.length}`);
      this.log(`🎨 [CSS_CAPTURE] ⚠️ Failed stylesheets (will use proxy): ${externalStylesheets.length}`);
      if (externalStylesheets.length > 0) {
        this.log(`🎨 [CSS_CAPTURE] Failed URLs: ${externalStylesheets.join(', ')}`);
      }
      this.log(`🎨 [CSS_CAPTURE] 📊 Computed styles captured: ${computedStyles.length}`);
      this.log(`🎨 [CSS_CAPTURE] 📦 Total CSS size: ${allCss.length} chars`);
      this.log(`🎨 [CSS_CAPTURE] ============================`);

      return htmlClone.outerHTML;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      this.error(`❌ [CSS_CAPTURE] Failed to capture CSS: ${errorMsg}`);
      return document.documentElement.outerHTML;
    }
  }

  /**
   * Fetch external CSS content and inline it
   * This implements the "Inline Everything" approach for reliable session replay
   */
  private async fetchExternalCSS(url: string): Promise<string | null> {
    try {
      this.log(`🎨 [CSS_FETCH] Attempting to fetch: ${url}`);
      this.log(`🎨 [CSS_FETCH] API URL: ${this.config.apiUrl}, API Key present: ${!!this.config.apiKey}`);

      // Try direct fetch first (might work for same-origin or CORS-enabled resources)
      let response: Response;
      let usedProxy = false;
      try {
        response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'omit'
        });
        this.log(`🎨 [CSS_FETCH] Direct fetch succeeded for: ${url}`);
      } catch (directError) {
        // Direct fetch failed, try via our CSS proxy
        const directErrorMsg = directError instanceof Error ? directError.message : String(directError);
        this.log(`🎨 [CSS_FETCH] Direct fetch failed: ${directErrorMsg}`);
        this.log(`🎨 [CSS_FETCH] Trying proxy fallback...`);

        const proxyUrl = this.buildApiUrl(`/proxy/css?url=${encodeURIComponent(url)}`);
        this.log(`🎨 [CSS_FETCH] Proxy URL: ${proxyUrl}`);

        try {
          response = await fetch(proxyUrl, {
            method: 'GET',
            headers: {
              'X-API-Key': this.config.apiKey || ''
            }
          });
          usedProxy = true;
          this.log(`🎨 [CSS_FETCH] Proxy fetch completed, status: ${response.status}`);
        } catch (proxyError) {
          const proxyErrorMsg = proxyError instanceof Error ? proxyError.message : String(proxyError);
          this.log(`❌ [CSS_FETCH] Proxy fetch also failed: ${proxyErrorMsg}`);
          throw proxyError;
        }
      }

      if (!response.ok) {
        this.log(`❌ [CSS_FETCH] Failed (${response.status} ${response.statusText}): ${url} via ${usedProxy ? 'proxy' : 'direct'}`);
        return null;
      }

      const cssText = await response.text();

      // Basic CSS processing
      if (cssText && cssText.trim()) {
        // Apply the same filtering we do for inline CSS
        const processedCSS = this.processInlinedCSS(cssText, url);
        this.log(`✅ [CSS_FETCH] Successfully fetched via ${usedProxy ? 'proxy' : 'direct'}: ${url} (${processedCSS.length} chars)`);
        return processedCSS;
      }

      this.log(`⚠️ [CSS_FETCH] Empty response from: ${url}`);
      return null;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      this.log(`❌ [CSS_FETCH] Error fetching: ${url} - ${errorMsg}`);
      return null;
    }
  }

  /**
   * Process inlined CSS to handle problematic rules
   */
  private processInlinedCSS(cssText: string, sourceUrl: string): string {
    try {
      // Apply same filtering logic as inline styles
      let processed = cssText;
      
      // Filter extreme font-sizes that break layout
      if (processed.includes('font-size') && processed.match(/font-size:\s*[5-9]\d*rem|font-size:\s*\d{2,}rem/)) {
        this.log(`Filtering extreme font-sizes in: ${sourceUrl}`);
        processed = processed.replace(/font-size:\s*([5-9]\d*|[1-9]\d{2,})rem/g, 'font-size: 2rem');
      }
      
      // Handle relative URLs in CSS (convert to absolute)
      processed = this.resolveRelativeUrlsInCSS(processed, sourceUrl);
      
      return processed;
    } catch (error) {
      this.log(`Error processing CSS from ${sourceUrl}:`, error);
      return cssText; // Return original if processing fails
    }
  }

  /**
   * Convert relative URLs in CSS to absolute URLs
   */
  private resolveRelativeUrlsInCSS(cssText: string, baseUrl: string): string {
    try {
      const base = new URL(baseUrl);
      
      // Replace url() references with absolute URLs
      return cssText.replace(/url\(['"]?([^'")]+)['"]?\)/g, (match, url) => {
        try {
          // Skip data URLs and already absolute URLs
          if (url.startsWith('data:') || url.startsWith('http') || url.startsWith('//')) {
            return match;
          }
          
          // Convert relative URL to absolute
          const absoluteUrl = new URL(url, base).href;
          return `url(${absoluteUrl})`;
        } catch (urlError) {
          // If URL parsing fails, return original
          return match;
        }
      });
    } catch (error) {
      this.log(`Error resolving relative URLs in CSS:`, error);
      return cssText;
    }
  }

  private captureWordPressStyles(): string | null {
    try {
      const wpStyles: string[] = [];
      
      // Look for WordPress-specific style elements
      const wpStyleElements = document.querySelectorAll('style[id*="wp"], style[id*="theme"], style[id*="custom"]');
      wpStyleElements.forEach(style => {
        if (style.textContent) {
          wpStyles.push(`/* WP Style: ${style.id || 'unknown'} */\n${style.textContent}`);
        }
      });
      
      // Look for WordPress theme customizer styles
      const customizerStyle = document.querySelector('#wp-custom-css');
      if (customizerStyle && customizerStyle.textContent) {
        wpStyles.push(`/* WP Customizer CSS */\n${customizerStyle.textContent}`);
      }
      
      // Look for WordPress theme styles in link elements
      const wpLinks = document.querySelectorAll('link[href*="wp-content"], link[href*="themes"]');
      wpLinks.forEach(link => {
        const href = (link as HTMLLinkElement).href;
        if (href && this.isSameOrigin(href)) {
          wpStyles.push(`/* WP Theme Stylesheet: ${href} */`);
        }
      });
      
      // Capture critical WordPress body/container styles
      const wpContainers = document.querySelectorAll('.wp-block, .entry-content, .site-main, .content-area, [class*="wp-"], [class*="theme-"]');
      wpContainers.forEach(container => {
        const computed = window.getComputedStyle(container);
        const ubId = container.getAttribute('data-ub-id');
        
        if (ubId) {
          const wpProps = [
            'max-width', 'margin', 'padding', 'background', 'color',
            'font-family', 'line-height', 'border'
          ];
          
          const cssProps = wpProps
            .map(prop => `${prop}: ${computed.getPropertyValue(prop)}`)
            .filter(prop => !prop.endsWith(': ') && !prop.endsWith(': initial'))
            .join('; ');
            
          if (cssProps) {
            wpStyles.push(`[data-ub-id="${ubId}"] { ${cssProps} }`);
          }
        }
      });
      
      return wpStyles.length > 0 ? wpStyles.join('\n') : null;
    } catch (error) {
      this.log('Error capturing WordPress styles:', error);
      return null;
    }
  }

  /**
   * Enhanced form-specific CSS capture for better replay appearance
   */
  private captureEnhancedFormCSS(): string | null {
    try {
      const formStyles: string[] = [];
      
      // Find all forms and their containers
      const allForms = document.querySelectorAll('form');
      
      allForms.forEach(form => {
        // Capture form container styles (section, div containing the form)
        const formContainer = form.closest('.u-section, .form-section, .contact-section, [class*="section"], [class*="container"]');
        if (formContainer) {
          const containerComputed = window.getComputedStyle(formContainer);
          const containerUbId = formContainer.getAttribute('data-ub-id');
          
          const containerProps = [
            'background', 'background-color', 'background-image', 'background-size',
            'padding', 'margin', 'width', 'max-width', 'min-height',
            'border', 'border-radius', 'box-shadow', 'position'
          ];
          
          const containerCSS = containerProps
            .map(prop => `${prop}: ${containerComputed.getPropertyValue(prop)}`)
            .filter(prop => !prop.endsWith(': ') && !prop.endsWith(': initial') && !prop.endsWith(': none'))
            .join('; ');
            
          if (containerCSS && containerUbId) {
            formStyles.push(`/* Form Container */\n[data-ub-id="${containerUbId}"] { ${containerCSS} }`);
          }
        }
        
        // Capture form-specific styles
        const formComputed = window.getComputedStyle(form);
        const formUbId = form.getAttribute('data-ub-id');
        
        if (formUbId) {
          const formProps = [
            'background', 'background-color', 'padding', 'margin', 'border',
            'border-radius', 'box-shadow', 'width', 'max-width'
          ];
          
          const formCSS = formProps
            .map(prop => `${prop}: ${formComputed.getPropertyValue(prop)}`)
            .filter(prop => !prop.endsWith(': ') && !prop.endsWith(': initial'))
            .join('; ');
            
          if (formCSS) {
            formStyles.push(`/* Form Element */\n[data-ub-id="${formUbId}"] { ${formCSS} }`);
          }
        }
        
        // Capture all form field styles
        const formFields = form.querySelectorAll('input, textarea, select, button, label');
        formFields.forEach(field => {
          const fieldComputed = window.getComputedStyle(field);
          const fieldUbId = field.getAttribute('data-ub-id');
          
          if (fieldUbId) {
            const fieldProps = [
              'background', 'background-color', 'border', 'border-radius',
              'padding', 'margin', 'font-size', 'font-family', 'color',
              'width', 'height', 'display', 'box-sizing'
            ];
            
            const fieldCSS = fieldProps
              .map(prop => `${prop}: ${fieldComputed.getPropertyValue(prop)}`)
              .filter(prop => !prop.endsWith(': ') && !prop.endsWith(': initial'))
              .join('; ');
              
            if (fieldCSS) {
              formStyles.push(`/* Form Field */\n[data-ub-id="${fieldUbId}"] { ${fieldCSS} }`);
            }
          }
        });
        
        // Capture NicePages-specific styles
        const nicePagesElements = form.querySelectorAll('[class*="u-"], .u-form, .u-btn, .u-section');
        nicePagesElements.forEach(element => {
          const computed = window.getComputedStyle(element);
          const ubId = element.getAttribute('data-ub-id');
          
          if (ubId) {
            const niceProps = [
              'background', 'background-color', 'background-image',
              'border', 'border-radius', 'color', 'font-size', 'font-weight',
              'padding', 'margin', 'text-align', 'cursor'
            ];
            
            const niceCSS = niceProps
              .map(prop => `${prop}: ${computed.getPropertyValue(prop)}`)
              .filter(prop => !prop.endsWith(': ') && !prop.endsWith(': initial'))
              .join('; ');
              
            if (niceCSS) {
              formStyles.push(`/* NicePages Element */\n[data-ub-id="${ubId}"] { ${niceCSS} }`);
            }
          }
        });
      });
      
      return formStyles.length > 0 ? `/* Enhanced Form CSS */\n${formStyles.join('\n')}` : null;
    } catch (error) {
      this.log('Error capturing enhanced form CSS:', error);
      return null;
    }
  }

  private getByteSize(str: string): number {
    return new Blob([str]).size;
  }

  /**
   * Filter out non-critical CSS to reduce payload size
   * Removes animations, transitions, and non-essential media queries
   */
  private filterNonCriticalCss(cssText: string): string {
    try {
      // Remove animation keyframes
      cssText = cssText.replace(/@keyframes[^{]+\{(?:[^{}]|\{[^}]*\})*\}/g, '');
      
      // Remove animation properties
      cssText = cssText.replace(/animation[^;]+;/g, '');
      cssText = cssText.replace(/transition[^;]+;/g, '');
      
      // Remove print media queries
      cssText = cssText.replace(/@media\s+print[^{]+\{(?:[^{}]|\{[^}]*\})*\}/g, '');
      
      // Remove very large media queries (likely desktop-only)
      cssText = cssText.replace(/@media[^{]*min-width:\s*120\d+px[^{]*\{(?:[^{}]|\{[^}]*\})*\}/g, '');
      
      // Remove hover states (not needed for replay)
      cssText = cssText.replace(/:[hH]over[^{]*\{[^}]*\}/g, '');
      
      // Remove focus states (not needed for replay)
      cssText = cssText.replace(/:focus[^{]*\{[^}]*\}/g, '');
      
      // Remove before/after pseudo elements with content (often decorative)
      cssText = cssText.replace(/::?(before|after)[^{]*\{[^}]*content:[^}]*\}/g, '');
      
      return cssText;
    } catch (error) {
      this.log('Error filtering CSS, returning original:', error);
      return cssText;
    }
  }

  /**
   * Minify HTML to reduce payload size while preserving functionality
   * Removes unnecessary whitespace, comments, and formatting
   */
  private minifyHtml(html: string): string {
    if (this.config.enableHtmlMinification === false) {
      return html;
    }

    try {
      const originalSize = this.getByteSize(html);
      
      // Tags that should preserve internal whitespace
      const preserveWhitespaceTags = ['pre', 'script', 'style', 'textarea'];
      
      // Create a map to store preserved content
      const preservedContent: Map<string, string> = new Map();
      let placeholderIndex = 0;
      
      // Preserve content in whitespace-sensitive tags
      preserveWhitespaceTags.forEach(tag => {
        const regex = new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'gi');
        html = html.replace(regex, (match) => {
          const placeholder = `__PRESERVE_${placeholderIndex++}__`;
          preservedContent.set(placeholder, match);
          return placeholder;
        });
      });
      
      // Remove HTML comments (except IE conditional comments)
      html = html.replace(/<!--(?!\[if).*?-->/gs, '');
      
      // Remove unnecessary whitespace between tags
      html = html.replace(/>\s+</g, '><');
      
      // Collapse multiple spaces to single space
      html = html.replace(/\s{2,}/g, ' ');
      
      // Remove whitespace around block-level elements
      const blockElements = ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'section', 'article', 'header', 'footer', 'nav', 'main', 'aside'];
      blockElements.forEach(tag => {
        html = html.replace(new RegExp(`\\s*<${tag}`, 'gi'), `<${tag}`);
        html = html.replace(new RegExp(`<\\/${tag}>\\s*`, 'gi'), `</${tag}>`);
      });
      
      // Remove unnecessary quotes in attributes (only where safe)
      // Only remove quotes around simple alphanumeric values
      html = html.replace(/(\w+)="([a-zA-Z0-9_-]+)"/g, '$1=$2');
      
      // But preserve quotes for data-ub-id (critical for our system)
      html = html.replace(/data-ub-id=([a-zA-Z0-9_-]+)/g, 'data-ub-id="$1"');
      
      // Remove empty attributes
      html = html.replace(/\s+[a-zA-Z-]+=""/g, '');
      
      // Trim attribute values
      html = html.replace(/="\s+/g, '="');
      html = html.replace(/\s+"/g, '"');
      
      // Restore preserved content
      preservedContent.forEach((content, placeholder) => {
        html = html.replace(placeholder, content);
      });
      
      const newSize = this.getByteSize(html);
      const reduction = ((originalSize - newSize) / originalSize * 100).toFixed(1);
      
      this.log(`HTML minified: ${originalSize} → ${newSize} bytes (${reduction}% reduction)`);
      
      return html;
    } catch (error) {
      this.log('Error minifying HTML, returning original:', error);
      return html;
    }
  }

  private stripLargeElements(html: string): string {
    // Remove large elements that aren't essential for replay
    return html
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '<script>/* script removed */</script>')
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '<style>/* style removed */</style>')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private processCssLinksForProxy(html: string): string {
    // Replace ALL external CSS links with proxied versions for playback compatibility
    // Key insight: Recording might be on site.com (same-origin for CSS)
    // but playback is ALWAYS on api.validiform.com (cross-origin for CSS)
    return html.replace(/<link\s+([^>]*href=["']([^"']+)["'][^>]*)>/gi, (match, attrs, href) => {
      // Only process stylesheet links - handle quotes or no quotes around 'stylesheet'
      // Matches: rel="stylesheet", rel='stylesheet', rel=stylesheet (no quotes)
      const isStylesheet = /rel\s*=\s*["']?stylesheet["']?/i.test(match);
      if (!isStylesheet) {
        return match;
      }

      // Skip data URLs and inline styles
      if (href.startsWith('data:') || href.startsWith('#')) {
        return match;
      }

      // Convert relative URLs to absolute URLs before proxying
      // This is CRITICAL: the CSS proxy requires absolute URLs
      // Relative URLs like "/_next/static/css/..." will fail with 400 error
      let absoluteHref = href;
      if (!href.startsWith('http://') && !href.startsWith('https://')) {
        try {
          // Use URL constructor to resolve relative URLs against page origin
          const baseUrl = window.location.origin;
          if (href.startsWith('/')) {
            // Root-relative URL (e.g., /_next/static/css/...)
            absoluteHref = baseUrl + href;
          } else if (href.startsWith('./') || href.startsWith('../')) {
            // Relative URL - resolve against current page
            absoluteHref = new URL(href, window.location.href).href;
          } else {
            // Relative to current directory
            absoluteHref = new URL(href, window.location.href).href;
          }
          this.log(`[CSS_PROXY] Converted relative URL to absolute: ${href} -> ${absoluteHref}`);
        } catch (error) {
          this.log(`[CSS_PROXY] Failed to convert relative URL: ${href}`, error);
          // Keep the original href if conversion fails
        }
      }

      // ALWAYS proxy external stylesheets for playback compatibility
      // Even same-origin CSS at recording time will be cross-origin during playback
      const proxyUrl = this.buildApiUrl(`/proxy/css?url=${encodeURIComponent(absoluteHref)}`);
      const wasSameOrigin = this.isSameOrigin(absoluteHref);
      this.log(`[CSS_PROXY] Processing ${wasSameOrigin ? 'same-origin' : 'cross-origin'} link: ${absoluteHref} -> proxy`);

      // Replace the href while preserving other attributes
      // Store original href (relative or absolute) for debugging
      const newAttrs = attrs.replace(/href=["'][^"']+["']/, `href="${proxyUrl}" data-original-href="${href}"`);
      return `<link ${newAttrs}>`;
    });
  }

  private extractCriticalCss(): string | null {
    try {
      const allStyles: string[] = [];
      
      // Get inline styles from style tags
      const styleTags = document.querySelectorAll('style');
      styleTags.forEach(styleTag => {
        if (styleTag.textContent) {
          allStyles.push(styleTag.textContent);
        }
      });
      
      // Get external stylesheets (only same-origin for security)
      const linkTags = document.querySelectorAll('link[rel="stylesheet"]');
      linkTags.forEach(linkTag => {
        try {
          const href = (linkTag as HTMLLinkElement).href;
          if (href && this.isSameOrigin(href)) {
            // For same-origin stylesheets, we can't access content directly
            // So we'll just note the URL
            allStyles.push(`/* External stylesheet: ${href} */`);
          }
        } catch (error) {
          // Ignore CORS errors
        }
      });
      
      const combinedCss = allStyles.join('\n');
      
      // Minify CSS
      return this.minifyCss(combinedCss);
    } catch (error) {
      this.error('Failed to extract CSS:', error);
      return null;
    }
  }

  private isSameOrigin(url: string): boolean {
    try {
      const urlObj = new URL(url, window.location.href);
      return urlObj.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  private minifyCss(css: string): string {
    return css
      .replace(/\/\*[\s\S]*?\*\//g, '') // Remove comments
      .replace(/\s+/g, ' ') // Collapse whitespace
      .replace(/;\s*}/g, '}') // Remove last semicolon in blocks
      .replace(/\s*{\s*/g, '{') // Clean up braces
      .replace(/\s*}\s*/g, '}')
      .replace(/\s*;\s*/g, ';') // Clean up semicolons
      .replace(/\s*:\s*/g, ':') // Clean up colons
      .trim();
  }

  private injectCssIntoHtml(html: string, css: string): string {
    const cssTag = `<style type="text/css">/* Captured CSS */\n${css}\n</style>`;
    
    // Try to inject into head
    if (html.includes('</head>')) {
      return html.replace('</head>', `${cssTag}\n</head>`);
    }
    
    // Fallback: inject at beginning of body
    if (html.includes('<body')) {
      return html.replace(/(<body[^>]*>)/, `$1\n${cssTag}`);
    }
    
    // Last resort: inject at beginning of html
    return cssTag + '\n' + html;
  }

  private setupAjaxInterception(): void {
    // Store reference to the recorder instance
    const recorder = this;
    
    // Intercept XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    
    XMLHttpRequest.prototype.open = function(method: string, url: string, ...args: any[]) {
      (this as any)._validiformMethod = method;
      (this as any)._validiformUrl = url;
      return (originalXHROpen as any).apply(this, [method, url, ...args]);
    };
    
    XMLHttpRequest.prototype.send = function(data: any) {
      const method = (this as any)._validiformMethod;
      const url = (this as any)._validiformUrl;
      
      // Check if this looks like a form submission
      if (method === 'POST' && data && recorder.sessionKey) {
        recorder.detectAjaxFormSubmission(data, url);
      }
      
      return originalXHRSend.call(this, data);
    };

    // Intercept fetch
    const originalFetch = window.fetch;
    window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
      if (init?.method === 'POST' && init?.body && recorder.sessionKey) {
        recorder.detectAjaxFormSubmission(init.body, input.toString());
      }
      return originalFetch.call(window, input, init);
    };
  }

  /**
   * Detect if an element is a submit-like button
   * Used for AJAX form capture when native submit event doesn't fire
   */
  private isSubmitButton(element: HTMLElement): boolean {
    // Check element type - buttons that are type="submit" or untyped (default is submit)
    if (element.tagName === 'BUTTON') {
      const type = (element as HTMLButtonElement).type;
      // Default button type is "submit", so only exclude explicit type="button"
      if (type !== 'button' && type !== 'reset') return true;
    }

    // Check input[type="submit"] or input[type="button"] (often used as submit)
    if (element.tagName === 'INPUT') {
      const type = (element as HTMLInputElement).type;
      if (type === 'submit') return true;
    }

    // Check common submit button classes
    const submitClasses = [
      'submit', 'btn-submit', 'form-submit', 'send', 'btn-send',
      'wpcf7-submit', 'gform_button', 'wpforms-submit',  // WordPress
      'u-btn-submit', 'u-btn-1', 'u-btn',  // NicePages
      'elementor-button', 'elementor-form-submit'  // Elementor
    ];

    if (submitClasses.some(cls => element.classList.contains(cls))) return true;

    // Check text content for submit-like words
    const text = element.textContent?.toLowerCase().trim() || '';
    const submitWords = ['submit', 'send', 'go', 'continue', 'next', 'sign up', 'register', 'subscribe', 'contact'];
    if (text && submitWords.some(word => text.includes(word))) return true;

    // Check for type="submit" attribute on any element
    if (element.getAttribute('type') === 'submit') return true;

    // Check parent for submit indicators (e.g., span inside a button)
    const parent = element.closest('button, [type="submit"], .submit, .btn-submit');
    if (parent && parent !== element) return true;

    return false;
  }

  /**
   * Find the form associated with a button element
   * Handles buttons inside forms, buttons with form attribute, and nearby forms
   */
  private findAssociatedForm(button: HTMLElement): HTMLFormElement | null {
    // 1. Check if button is within a form
    const parentForm = button.closest('form');
    if (parentForm) return parentForm as HTMLFormElement;

    // 2. Check for form attribute (button outside form)
    const formId = button.getAttribute('form');
    if (formId) {
      const form = document.getElementById(formId);
      if (form instanceof HTMLFormElement) return form;
    }

    // 3. Look for nearby form (within same container)
    const container = button.closest('div, section, article, .form-container, .contact-form, .u-form');
    if (container) {
      const nearbyForm = container.querySelector('form');
      if (nearbyForm) return nearbyForm as HTMLFormElement;
    }

    return null;
  }

  /**
   * Capture form data when submit button is clicked (for AJAX forms)
   * This captures IMMEDIATELY before AJAX can clear the fields
   */
  private captureVirtualSubmission(form: HTMLFormElement, trigger: HTMLElement): void {
    // Generate a capture key for deduplication
    const formKey = form.getAttribute('data-ub-id') || form.id || form.className || 'form';
    const now = Date.now();

    // Check if we recently captured this form (within 2 seconds)
    // This prevents double-capture if native submit also fires
    for (const key of this.recentCaptures) {
      if (key.startsWith(formKey) && now - parseInt(key.split('-')[1] || '0') < 2000) {
        this.log('Skipping virtual submission - recently captured:', formKey);
        return;
      }
    }

    // Check if a native submit recently fired (within 500ms)
    if (now - this.lastSubmissionTime < 500) {
      this.log('Skipping virtual submission - native submit just fired');
      return;
    }

    // Mark as captured
    const captureKey = `${formKey}-${now}`;
    this.recentCaptures.add(captureKey);
    setTimeout(() => this.recentCaptures.delete(captureKey), 3000);

    this.log('🎯 Capturing virtual submission triggered by button click:', trigger.className);

    // Get all input values from the form
    const inputs = form.querySelectorAll('input, textarea, select');
    const fields: any[] = [];

    inputs.forEach((element) => {
      const input = element as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

      // Skip non-data inputs
      if (input.type === 'submit' || input.type === 'button' || input.type === 'reset' || input.type === 'hidden') return;
      if (!this.config.trackSensitiveData && input.type === 'password') return;

      // Get the value based on input type
      let value = '';
      if (input.type === 'checkbox') {
        value = (input as HTMLInputElement).checked ? 'checked' : '';
      } else if (input.type === 'radio') {
        if ((input as HTMLInputElement).checked) {
          value = input.value;
        } else {
          return; // Skip unchecked radios
        }
      } else {
        value = input.value || '';
      }

      // Only include fields with values
      if (value) {
        fields.push({
          name: input.name || input.id || `field_${fields.length}`,
          value: input.type === 'password' ? '***' : value,
          type: input.type || 'text',
          label: this.findFieldLabel(input as HTMLElement),
          required: input.required
        });
      }
    });

    // Capture TCPA consent on this path too. Gravity Forms (and other AJAX
    // forms) submit from a button click, so without this the native-submit
    // path never runs and consent is never recorded. captureTcpaConsent()
    // de-dupes internally, so a native submit for the same submission — this
    // path's own recentCaptures/lastSubmissionTime guards only cover the
    // opposite ordering — will not produce a second consent record.
    //
    // Deliberately ahead of the empty-fields early return below: an unchecked
    // consent box on an otherwise-empty form yields zero collectable fields,
    // and a declined consent is exactly the record worth keeping. On a pure
    // AJAX form nothing else would capture it.
    const tcpaUbId = parseInt(form.getAttribute('data-ub-id') || '', 10);
    if (Number.isFinite(tcpaUbId)) {
      this.captureTcpaConsent(form, tcpaUbId);
    }

    if (fields.length === 0) {
      this.log('No fields found for virtual submission');
      return;
    }

    this.log(`Virtual submission capturing ${fields.length} fields`);

    // Send to form-submissions endpoint
    const submissionData = {
      sessionKey: this.sessionKey,
      projectId: this.config.projectId,
      formId: form.getAttribute('data-ub-id') || form.id,
      formName: this.detectFormName(form) || 'Form Submission',
      url: window.location.href,
      fields,
      submittedAt: new Date().toISOString(),
      source: 'button-click',
      visitor: {
        id: this.config.visitorId,
        key: this.config.visitorKey
      },
      metadata: {
        triggerElement: trigger.tagName,
        triggerClass: trigger.className,
        captureMethod: 'virtual-submission'
      }
    };

    // Send asynchronously
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey;
    }

    fetch(this.buildApiUrl(`/projects/${this.config.projectId}/form-submissions`), {
      method: 'POST',
      headers,
      body: JSON.stringify(submissionData),
      keepalive: true
    }).then(response => {
      if (response.ok) {
        this.log('✅ Virtual submission sent successfully');
      } else {
        this.error('Virtual submission failed:', response.status);
      }
    }).catch(error => {
      this.error('Virtual submission error:', error);
    });
  }

  /**
   * Find the label for an input field
   */
  private findFieldLabel(input: HTMLElement): string {
    // Check for associated label via for attribute
    const id = input.id;
    if (id) {
      const label = document.querySelector(`label[for="${id}"]`);
      if (label) return label.textContent?.trim() || '';
    }

    // Check for parent label
    const parentLabel = input.closest('label');
    if (parentLabel) {
      // Get text content excluding the input itself
      const clone = parentLabel.cloneNode(true) as HTMLElement;
      const inputs = clone.querySelectorAll('input, select, textarea');
      inputs.forEach(el => el.remove());
      return clone.textContent?.trim() || '';
    }

    // Check for previous sibling label
    const prev = input.previousElementSibling;
    if (prev && prev.tagName === 'LABEL') {
      return prev.textContent?.trim() || '';
    }

    // Check for aria-label
    const ariaLabel = input.getAttribute('aria-label');
    if (ariaLabel) return ariaLabel;

    // Check for placeholder as fallback
    const placeholder = input.getAttribute('placeholder');
    if (placeholder) return placeholder;

    // Use name or id as last resort
    return (input as HTMLInputElement).name || input.id || '';
  }

  /**
   * Detect form name from various sources
   */
  private detectFormName(form: HTMLFormElement): string {
    // Check for data-form-name attribute
    const dataName = form.getAttribute('data-form-name');
    if (dataName) return dataName;

    // Check for name attribute
    if (form.name) return form.name;

    // Check for id
    if (form.id) return form.id;

    // Check for heading inside or near the form
    const heading = form.querySelector('h1, h2, h3, h4, h5, h6');
    if (heading) return heading.textContent?.trim() || '';

    // Check parent container for heading
    const container = form.closest('div, section');
    if (container) {
      const containerHeading = container.querySelector('h1, h2, h3, h4, h5, h6');
      if (containerHeading) return containerHeading.textContent?.trim() || '';
    }

    return '';
  }

  private setupFormButtonTracking(): void {
    // Track button clicks and capture form data for AJAX forms
    // This captures IMMEDIATELY when a submit button is clicked,
    // before AJAX can clear the form fields
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;

      // Use improved submit button detection
      if (this.isSubmitButton(target)) {
        const form = this.findAssociatedForm(target);

        if (form) {
          this.log('Submit button clicked:', target.className);

          // Capture form data if captureOnButtonClick is enabled (default: true)
          if (this.config.captureOnButtonClick !== false) {
            // Capture IMMEDIATELY - before AJAX can clear the fields
            this.captureVirtualSubmission(form, target);
          } else {
            this.log('captureOnButtonClick disabled - waiting for native submit event');
          }
        }
      }
    }, true);
  }

  private detectAjaxFormSubmission(data: any, url: string): void {
    try {
      // Look for form data patterns
      let isFormSubmission = false;
      let formData: Record<string, any> = {};

      if (data instanceof FormData) {
        isFormSubmission = true;
        data.forEach((value, key) => {
          formData[key] = value;
        });
      } else if (typeof data === 'string') {
        // Check for URL-encoded form data
        if (data.includes('=') && data.includes('&')) {
          isFormSubmission = true;
          const params = new URLSearchParams(data);
          params.forEach((value, key) => {
            formData[key] = value;
          });
        }
      } else if (data && typeof data === 'object') {
        // JSON form data
        isFormSubmission = true;
        formData = data;
      }

      if (isFormSubmission && Object.keys(formData).length > 0) {
        this.log('AJAX form submission detected:', url, formData);
        
        // Try to find the form that might have been submitted
        const forms = document.querySelectorAll('form');
        let matchingForm: HTMLFormElement | null = null;
        
        for (const form of Array.from(forms)) {
          const formFields = new FormData(form);
          let fieldMatches = 0;
          let totalFields = 0;
          
          formFields.forEach((value, name) => {
            totalFields++;
            if (formData[name] !== undefined) {
              fieldMatches++;
            }
          });
          
          // If more than 50% of fields match, consider this the submitted form
          if (totalFields > 0 && fieldMatches / totalFields > 0.5) {
            matchingForm = form;
            break;
          }
        }
        
        if (matchingForm) {
          const ubId = matchingForm.getAttribute('data-ub-id');
          if (ubId) {
            this.log(`AJAX form submission detected for form ${ubId} - already captured by submit event`);
            // DO NOT CAPTURE HERE - the form submit event already handled it
          }
        }
      }
    } catch (error) {
      this.log('Error detecting AJAX form submission:', error);
    }
  }

  private checkForFormSubmissionAfterClick(form: HTMLFormElement, _button: HTMLElement): void {
    // Check if form values have been cleared (common after successful submission)
    const inputs = form.querySelectorAll('input[type="text"], input[type="email"], textarea');
    let clearedFields = 0;
    
    inputs.forEach(input => {
      if ((input as HTMLInputElement).value === '') {
        clearedFields++;
      }
    });
    
    // If many fields are cleared, this might indicate a successful submission
    if (inputs.length > 0 && clearedFields / inputs.length > 0.5) {
      this.log('Form fields cleared after button click - possible submission');
      // DO NOT CAPTURE HERE - let the actual submit event handle it
    }
  }

  /**
   * Initialize the new form detection architecture
   * Registers available detectors and sets up the registry
   */
  private initializeFormDetectors(): void {
    this.log('Initializing form detection architecture');
    
    // Register available form detectors
    try {
      // Register WordPress detector (highest priority)
      const wordPressDetector = new WordPressDetector(this);
      this.formDetectorRegistry.register(wordPressDetector, {
        enabled: true,
        priority: wordPressDetector.priority
      });

      // Register NicePages detector (high priority) 
      const nicePagesDetector = new NicePagesDetector(this);
      this.formDetectorRegistry.register(nicePagesDetector, {
        enabled: true,
        priority: nicePagesDetector.priority
      });

      this.log('Registered form detectors:', this.formDetectorRegistry.getDetectorNames());
      
    } catch (error) {
      this.error('Error registering form detectors:', error);
    }
    
    // Initialize the registry (will set up any registered detectors)
    this.formDetectorRegistry.initialize();
    
    this.log('Form detection architecture initialized');
  }

  private setupWordPressFormDetection(): void {
    // Detect WordPress form plugins
    this.log('Setting up WordPress form detection');
    
    // Monitor for WordPress AJAX submissions
    this.interceptWordPressAjax();
    
    // Set up success message monitoring
    this.monitorFormSuccessMessages();
    
    // Watch for common WordPress form patterns
    this.detectWordPressFormPatterns();
  }

  private setupNicePagesFormDetection(): void {
    this.log('Setting up NicePages form detection');
    
    // Detect NicePages forms by action URL and class patterns
    const nicePagesSelectors = [
      'form[action*="nicepagesrv.com"]',
      'form[action*="nicepage"]', 
      'form.u-form',
      'form[class*="u-form"]'
    ];
    
    nicePagesSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(form => {
        this.log('NicePages form detected:', selector, form);
        
        // Monitor form submission
        form.addEventListener('submit', (_e) => {
          this.log('NicePages form submission detected');
          const ubId = form.getAttribute('data-ub-id');
          if (ubId) {
            this.captureFormSubmission(form as HTMLFormElement, parseInt(ubId));
          }
        });
        
        // Monitor submit button clicks (NicePages often uses custom submit handling)
        const submitSelectors = [
          '.u-btn-submit',
          'input[type="submit"]', 
          'button[type="submit"]',
          '[class*="submit"]'
        ];
        
        submitSelectors.forEach(btnSelector => {
          const submitBtn = form.querySelector(btnSelector);
          if (submitBtn) {
            submitBtn.addEventListener('click', () => {
              this.log('NicePages submit button clicked:', btnSelector);
              setTimeout(() => {
                this.checkForNicePagesSuccess(form as HTMLFormElement);
              }, 1000);
            });
          }
        });
      });
    });
  }

  private checkForNicePagesSuccess(form: HTMLFormElement): void {
    // Check for NicePages success message patterns
    const successSelectors = [
      '.u-form-send-success',
      '.u-form-send-message.u-form-send-success',
      '[class*="success"]',
      '[class*="thank"]'
    ];
    
    for (const selector of successSelectors) {
      const successElement = form.querySelector(selector);
      if (successElement) {
        const element = successElement as HTMLElement;
        // Check if element is visible (not hidden)
        if (element.style.display !== 'none' && 
            element.offsetParent !== null && 
            element.textContent?.trim()) {
          this.log('NicePages form success detected:', selector, element.textContent);
          const ubId = form.getAttribute('data-ub-id');
          if (ubId) {
            this.captureFormSubmission(form, parseInt(ubId));
          }
          return;
        }
      }
    }
    
    // Also check if form fields were cleared (another success indicator)
    const inputs = form.querySelectorAll('input[type="text"], input[type="email"], textarea');
    let clearedFields = 0;
    inputs.forEach(input => {
      if ((input as HTMLInputElement).value === '') {
        clearedFields++;
      }
    });
    
    if (inputs.length > 0 && clearedFields / inputs.length > 0.7) {
      this.log('NicePages form success detected via field clearing');
      const ubId = form.getAttribute('data-ub-id');
      if (ubId) {
        this.captureFormSubmission(form, parseInt(ubId));
      }
    }
  }

  private interceptWordPressAjax(): void {
    const recorder = this;
    
    // Intercept WordPress admin-ajax.php calls
    const originalFetch = window.fetch;
    window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
      const url = input.toString();
      
      // Check for WordPress AJAX endpoints
      if (url.includes('admin-ajax.php') || url.includes('wp-json') || url.includes('wp-admin/admin-ajax.php')) {
        if (init?.method === 'POST' && init?.body) {
          recorder.log('WordPress AJAX detected:', url);
          recorder.detectWordPressFormSubmission(init.body, url);
        }
      }
      
      return originalFetch.call(window, input, init);
    };
    
    // Also intercept XMLHttpRequest for older WordPress forms
    const originalXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function(data: any) {
      const url = (this as any)._validiformUrl || '';
      
      if (url.includes('admin-ajax.php') || url.includes('wp-json')) {
        recorder.log('WordPress XHR AJAX detected:', url);
        recorder.detectWordPressFormSubmission(data, url);
      }
      
      return originalXHRSend.call(this, data);
    };
  }

  private detectWordPressFormSubmission(data: any, url: string): void {
    try {
      let formData: Record<string, any> = {};
      let actionType = '';
      
      // Parse form data based on type
      if (data instanceof FormData) {
        data.forEach((value, key) => {
          formData[key] = value;
        });
      } else if (typeof data === 'string') {
        const params = new URLSearchParams(data);
        params.forEach((value, key) => {
          formData[key] = value;
        });
      } else if (data && typeof data === 'object') {
        formData = data;
      }
      
      // Get WordPress action type
      actionType = formData.action || '';
      
      // Common WordPress form actions
      const wpFormActions = [
        'wpcf7_submit',           // Contact Form 7
        'gform_submit',          // Gravity Forms  
        'wpforms_submit',        // WPForms
        'ninja_forms_ajax_submit', // Ninja Forms
        'forminator_submit_form', // Forminator
        'fluentform_submit',     // Fluent Forms
        'elementor_pro_forms',   // Elementor Pro Forms
        'contact-form'           // Generic contact forms
      ];
      
      if (wpFormActions.includes(actionType) || url.includes('contact') || url.includes('form')) {
        this.log('WordPress form submission detected!', {
          action: actionType,
          url: url,
          fields: Object.keys(formData).length
        });
        
        // Try to find the corresponding form element
        this.findAndCaptureWordPressForm(formData, actionType);
        
        // Set up success message monitoring
        setTimeout(() => {
          this.checkForWordPressFormSuccess();
        }, 500);
      }
    } catch (error) {
      this.log('Error detecting WordPress form submission:', error);
    }
  }

  private findAndCaptureWordPressForm(formData: Record<string, any>, actionType: string): void {
    // Try to match the submitted data to a form on the page
    const forms = document.querySelectorAll('form');
    let matchingForm: HTMLFormElement | null = null;
    let bestMatchScore = 0;
    
    for (const form of Array.from(forms)) {
      // Check for WordPress form plugin specific classes/attributes
      const isWPForm = form.classList.contains('wpcf7-form') ||
                      form.classList.contains('gform_wrapper') ||
                      form.classList.contains('wpforms-form') ||
                      form.classList.contains('ninja-forms-form') ||
                      form.classList.contains('forminator-custom-form') ||
                      form.classList.contains('fluentform') ||
                      form.classList.contains('elementor-form') ||
                      form.querySelector('input[name="action"]');
      
      if (isWPForm) {
        // Calculate field match score
        const formFields = new FormData(form);
        let fieldMatches = 0;
        let totalFormFields = 0;
        
        formFields.forEach((value, name) => {
          totalFormFields++;
          // Check if field name exists in submitted data (ignoring WordPress-specific fields)
          if (!name.startsWith('_wp') && !name.startsWith('action') && formData[name] !== undefined) {
            fieldMatches++;
          }
        });
        
        const matchScore = totalFormFields > 0 ? fieldMatches / totalFormFields : 0;
        
        if (matchScore > bestMatchScore && matchScore > 0.3) { // At least 30% field match
          bestMatchScore = matchScore;
          matchingForm = form;
        }
      }
    }
    
    if (matchingForm) {
      const ubId = matchingForm.getAttribute('data-ub-id');
      if (ubId) {
        this.log('Found matching WordPress form for submission');
        this.captureFormSubmission(matchingForm, parseInt(ubId));
      }
    } else {
      // Create a synthetic form submission record
      this.log('Creating synthetic WordPress form submission record');
      this.captureSyntheticFormSubmission(formData, actionType);
    }
  }

  private captureSyntheticFormSubmission(formData: Record<string, any>, actionType: string): void {
    // Create a synthetic form submission when we can't find the actual form
    const fields = Object.entries(formData)
      .filter(([key]) => !key.startsWith('_wp') && !key.startsWith('action'))
      .map(([name, value]) => ({
        name,
        value: value.toString(),
        type: 'text',
        label: name.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        required: false
      }));
    
    const submissionData = {
      sessionKey: this.sessionKey!,
      projectId: this.config.projectId,
      formId: 'wp-' + actionType,
      formName: 'WordPress Form (' + actionType + ')',
      fields: fields,
      rawData: formData,
      url: window.location.href,
      visitor: {
        id: this.config.visitorId || 0,
        key: this.config.visitorKey || '',
        ip: '',
        userAgent: navigator.userAgent
      },
      source: document.referrer ? new URL(document.referrer).hostname : 'direct',
      referrer: document.referrer,
      metadata: {
        formAction: actionType,
        formMethod: 'POST',
        pageTitle: document.title,
        wordpressForm: true
      }
    };
    
    // Send synthetic form submission data
    const headers: any = {
      'Content-Type': 'application/json'
    };
    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey;
    }
    
    fetch(this.buildApiUrl(`/projects/${this.config.projectId}/form-submissions`), {
      method: 'POST',
      headers,
      body: JSON.stringify(submissionData),
      keepalive: true
    }).then(response => {
      if (response.ok) {
        this.log('WordPress form submission data captured successfully');
      }
    }).catch(error => {
      this.error('Failed to capture WordPress form submission data:', error);
    });
  }

  private monitorFormSuccessMessages(): void {
    // Watch for common success message patterns
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              
              // Check for success message patterns
              const successPatterns = [
                '.wpcf7-mail-sent-ok',        // Contact Form 7 success
                '.gform_confirmation_message', // Gravity Forms success
                '.wpforms-confirmation-container', // WPForms success
                '.ninja-forms-success-msg',   // Ninja Forms success
                '.forminator-response-message', // Forminator success
                '.ff_form_success',           // Fluent Forms success
                '.u-form-send-success',       // NicePages success
                '.u-form-send-message',       // NicePages message
                '[class*="success"]',         // Generic success classes
                '[class*="thank"]',           // Thank you messages
                '[class*="confirm"]'          // Confirmation messages
              ];
              
              for (const pattern of successPatterns) {
                if (element.matches && element.matches(pattern)) {
                  this.log('Form success message detected:', pattern, element.textContent);
                  this.handleFormSuccessDetected(element);
                  return;
                }
                
                // Also check if any child elements match
                const successElement = element.querySelector(pattern);
                if (successElement) {
                  this.log('Form success message detected in child:', pattern, successElement.textContent);
                  this.handleFormSuccessDetected(successElement);
                  return;
                }
              }
            }
          });
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  private handleFormSuccessDetected(_element: Element): void {
    this.log('Form submission success confirmed via DOM changes');
    
    // Try to find the form that was submitted
    const forms = document.querySelectorAll('form');
    for (const form of Array.from(forms)) {
      // Look for forms that are now hidden or have success states
      if (form.style.display === 'none' || 
          form.classList.contains('submitted') ||
          form.classList.contains('success') ||
          form.querySelector('.success, .submitted, [style*="display: none"]')) {
        
        const ubId = form.getAttribute('data-ub-id');
        if (ubId) {
          this.log('Found submitted form via success message detection');
          this.captureFormSubmission(form, parseInt(ubId));
          break;
        }
      }
    }
  }

  private checkForWordPressFormSuccess(): void {
    // Check for immediate success indicators
    const successSelectors = [
      '.wpcf7-mail-sent-ok:not([style*="display: none"])',
      '.gform_confirmation_message:visible',
      '.wpforms-confirmation-container:visible',
      '.ninja-forms-success-msg:visible',
      '.forminator-response-message:visible',
      '.ff_form_success:visible'
    ];
    
    for (const selector of successSelectors) {
      try {
        const successElement = document.querySelector(selector);
        if (successElement && (successElement as HTMLElement).offsetParent !== null) { // Check if visible
          this.log('WordPress form success detected:', selector);
          this.handleFormSuccessDetected(successElement);
          break;
        }
      } catch (error) {
        // Ignore selector errors
      }
    }
  }

  private detectWordPressFormPatterns(): void {
    // Monitor for WordPress-specific form behaviors
    const forms = document.querySelectorAll('form');
    
    forms.forEach(form => {
      // Check if this looks like a WordPress form
      const isWPForm = form.classList.contains('wpcf7-form') ||
                      form.classList.contains('gform_wrapper') ||
                      form.classList.contains('wpforms-form') ||
                      form.classList.contains('ninja-forms-form') ||
                      form.classList.contains('forminator-custom-form') ||
                      form.classList.contains('fluentform') ||
                      form.classList.contains('elementor-form') ||
                      form.querySelector('input[name="action"]');
      
      if (isWPForm) {
        this.log('WordPress form detected:', form.className);
        
        // Add special handling for WordPress forms
        form.addEventListener('submit', (_event) => {
          this.log('WordPress form submit event captured');
          
          // WordPress forms often prevent default and handle via AJAX
          // Set up monitoring for the submission
          setTimeout(() => {
            this.checkForWordPressFormSuccess();
          }, 1000);
        });
        
        // Monitor for loading states
        const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
        if (submitButton) {
          submitButton.addEventListener('click', () => {
            this.log('WordPress form submit button clicked');
            
            // Monitor for form state changes
            setTimeout(() => {
              this.checkForWordPressFormStateChanges(form);
            }, 2000);
          });
        }
      }
    });
  }

  private checkForWordPressFormStateChanges(form: HTMLFormElement): void {
    // Check if form has been disabled or shows loading state
    const isDisabled = form.querySelector('input[disabled], button[disabled]');
    const hasLoader = form.querySelector('.loading, .spinner, [class*="load"]');
    const isHidden = form.style.display === 'none' || form.classList.contains('hidden');
    
    if (isDisabled || hasLoader || isHidden) {
      this.log('WordPress form state change detected - likely submitted');
      
      const ubId = form.getAttribute('data-ub-id');
      if (ubId) {
        this.captureFormSubmission(form, parseInt(ubId));
      }
    }
  }

  private setupCustomEventInterception(): void {
    // Intercept custom JavaScript events that might indicate form submissions
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    const recorder = this;
    
    EventTarget.prototype.addEventListener = function(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) {
      // Monitor for custom form-related events
      const formEventTypes = ['formsubmit', 'formsent', 'formcomplete', 'success', 'submitted'];
      
      if (formEventTypes.includes(type.toLowerCase())) {
        recorder.log('Custom form event listener detected:', type);
        
        // Wrap the original listener to capture the event
        const wrappedListener = function(this: any, event: Event) {
          recorder.log('Custom form event fired:', type, event);
          
          // Try to find related form
          const target = event.target as Element;
          const form = target.closest('form') || document.querySelector('form');
          
          if (form) {
            const ubId = form.getAttribute('data-ub-id');
            if (ubId) {
              recorder.log('Capturing form submission from custom event');
              recorder.captureFormSubmission(form, parseInt(ubId));
            }
          }
          
          // Call original listener
          if (typeof listener === 'function') {
            listener.call(this, event);
          } else if (listener && typeof listener.handleEvent === 'function') {
            listener.handleEvent(event);
          }
        };
        
        return originalAddEventListener.call(this, type, wrappedListener, options);
      }
      
      return originalAddEventListener.call(this, type, listener, options);
    };
  }

  // Static initialization method for easy embedding
  static init(config: RecorderConfig): ValidiformRecorder {
    // Check if already initialized
    if ((window as any).validiformRecorder && (window as any).validiformRecorder.isRecording) {
      console.log('[ValidiformRecorder] Already initialized and recording');
      return (window as any).validiformRecorder;
    }
    
    const recorder = new ValidiformRecorder(config);
    
    // Store instance globally for debugging and access
    (window as any).validiformRecorder = recorder;
    
    if (config.debug && window.location.href.includes('60-20.com')) {
      console.log('[ValidiformRecorder] [DEBUG] Initializing recorder for 60-20.com');
      console.log('[ValidiformRecorder] [DEBUG] Config:', config);
      console.log('[ValidiformRecorder] [DEBUG] Document ready state:', document.readyState);
      console.log('[ValidiformRecorder] [DEBUG] Stored instance at window.validiformRecorder');
    }
    
    // Auto-start on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        recorder.start().catch(console.error);
        if (config.debug) console.log('[ValidiformRecorder] [DEBUG] Started after DOMContentLoaded');
      });
    } else {
      recorder.start().catch(console.error);
    }

    // Auto-stop on page unload - only if fully initialized and recording
    window.addEventListener('beforeunload', () => {
      if (recorder.isRecording && recorder.isInitialized && recorder.sessionKey) {
        recorder.stop();
      }
    });

    return recorder;
  }
}

// Export for various module systems
export default ValidiformRecorder;

// Make it available globally for browser usage
if (typeof window !== 'undefined') {
  (window as any).ValidiformRecorder = ValidiformRecorder;
}